// API Service for ChatBot

import axios from "axios";
import { END_POINTS } from "constants/APIConstant";

// Helper function to detect language (Arabic or English)
const detectLanguage = (text) => {
  if (!text || text.trim().length === 0) return "ar"; // Default to Arabic

  // Remove whitespace and special characters for detection
  const cleanText = text.trim();

  // Check for Arabic characters (Unicode range: \u0600-\u06FF)
  const arabicRegex = /[\u0600-\u06FF]/;

  // Count Arabic characters
  const arabicMatches = cleanText.match(arabicRegex);
  const arabicCount = arabicMatches ? arabicMatches.length : 0;

  // Check for English characters (a-z, A-Z)
  const englishRegex = /[a-zA-Z]/;
  const englishMatches = cleanText.match(englishRegex);
  const englishCount = englishMatches ? englishMatches.length : 0;

  // If any Arabic characters exist, it's Arabic
  if (arabicCount > 0) {
    return "ar";
  }

  // If no Arabic and has English, it's English
  if (englishCount > 0) {
    return "en";
  }

  // Default to Arabic if no clear language detected
  return "ar";
};

// API Version Configuration - Switch between v1 and v2
// v1: Uses /api/messages endpoint with conv_id
// v2: Uses /v2/query endpoint with query parameter and includes info status messages
export const API_VERSION = "v1"; // Change to "v1" or "v2"

// تخزين آخر conv_id معروف لكل محادثة (chatId) — fallback عندما الهيدر x-conversation-id غير متاح (مثلاً CORS) أو الاستجابة لا تعيد القيمة
const conversationIdByChatId = new Map();

// Helper function to parse SSE event line (v1 format: "event: Final_Answer" or "event: labour_migration_agent")
const parseSSEEvent = (line) => {
  const t = line.trim();
  if (t.startsWith("event: ") || t.startsWith("event:")) {
    const after = t.startsWith("event: ") ? t.slice(7) : t.slice(6);
    return after.trim() || null;
  }
  return null;
};

// Helper function to parse SSE data
const parseSSEData = (line) => {
  let content = null;

  // Extract content after "data: ", or "data:"
  if (line.startsWith(`data: `)) {
    content = line.substring(6);
  } else if (line.startsWith("data:")) {
    content = line.substring(5);
  } else {
    return null;
  }

  // If line is only "data:" or "data: " with no other content, return one space
  if (!content || content.trim() === "") {
    return " ";
  }

  // If there's content, return it
  return content;
};

// Helper function to parse JSON-encoded strings (fixes double quotation issue)
const parseJSONString = (str) => {
  try {
    // Check if the string starts and ends with quotes (indicating it's a JSON string)
    if (str.startsWith('"') && str.endsWith('"')) {
      // Parse the JSON string to get the actual content
      return JSON.parse(str);
    }
    return str;
  } catch (e) {
    // If parsing fails, return the original string
    return str;
  }
};

// <br> = newline everywhere. Stream can split as "202" + "1" + "<br" + ">" (chunks may add \n → "202\n1\n<br\n>").
// (0) Collapse whitespace between digits so "202\n1<br>" → "2021<br>"; (1) digits+<br> → digits+\n; (2) any-char+<br>; (3) standalone <br>.
const replaceBrTags = (text) => {
  if (!text || text.length === 0) return text;
  let t = text.replace(/(\d)\s+(\d)/g, "$1$2");
  return t
    .replace(/(\d+)<\s*br\s*\/?\s*>\s*/gi, "$1\n")
    .replace(/(.)<\s*br\s*\/?\s*>\s*/gi, "$1\n")
    .replace(/<\s*br\s*\/?\s*>\s*/gi, "\n");
};

// Helper: add spaces between Arabic words, numbers, and punctuation
// But preserve decimal numbers (like 7.25), newlines, and Markdown links
const addSpacesBetweenWords = (text) => {
  if (!text || text.length === 0) return text;

  let result = text;

  // First, preserve Markdown links [text](url) by temporarily replacing them
  const markdownLinkPlaceholders = [];
  let markdownLinkIndex = 0;

  // Match Markdown links: [text](url) - handle incomplete links too
  result = result.replace(
    /\[([^\]]*)\]\(([^)]*)\)/g,
    (match, linkText, url) => {
      const placeholder = `__MARKDOWN_LINK_${markdownLinkIndex}__`;
      markdownLinkPlaceholders.push({ placeholder, linkText, url });
      markdownLinkIndex++;
      return placeholder;
    },
  );

  // Preserve decimal numbers by temporarily replacing them
  const decimalPlaceholders = [];
  let decimalIndex = 0;

  // Match decimal numbers (like 7.25, 3.14, etc.)
  result = result.replace(/(\d+\.\d+)/g, (match) => {
    const placeholder = `__DECIMAL_${decimalIndex}__`;
    decimalPlaceholders.push({ placeholder, value: match });
    decimalIndex++;
    return placeholder;
  });

  // Add spaces between Arabic characters and numbers
  result = result
    // Add space between Arabic word and number
    .replace(/([\u0600-\u06FF])([0-9])/g, "$1 $2")
    // Add space between number and Arabic word
    .replace(/([0-9])([\u0600-\u06FF])/g, "$1 $2");

  // Add spaces between numbers and punctuation (but not decimal point)
  result = result
    // Add space between number and punctuation (:, %, ،) but not decimal point
    .replace(/([0-9])([:%،])/g, "$1 $2")
    // Add space between punctuation (:, ،) and number (but not decimal point)
    .replace(/([:،])([0-9])/g, "$1 $2");

  // Add spaces between Arabic words and punctuation
  result = result
    // Add space between Arabic word and punctuation
    .replace(/([\u0600-\u06FF])([:%،.])/g, "$1 $2")
    // Add space between punctuation and Arabic word
    .replace(/([:%،.])([\u0600-\u06FF])/g, "$1 $2");

  // Restore decimal numbers
  decimalPlaceholders.forEach(({ placeholder, value }) => {
    result = result.replace(placeholder, value);
  });

  // Restore Markdown links
  markdownLinkPlaceholders.forEach(({ placeholder, linkText, url }) => {
    result = result.replace(placeholder, `[${linkText}](${url})`);
  });

  // Clean up multiple spaces but preserve newlines
  // Replace multiple spaces/tabs with single space, but keep newlines
  result = result.replace(/[ \t]+/g, " ");

  return result;
};

// Helper: process SSE chunk text (convert <br> to new lines, add spaces, keep markdown)
const processSSEContent = (text) => {
  if (!text || text.length === 0) return text;

  // First, convert <br> to newline
  let result = replaceBrTags(text);

  // Preserve * bullet points before any processing
  // Keep * as is, just ensure proper spacing (don't add newlines - only preserve existing ones)
  const bulletPlaceholder = "__BULLET_POINT__";
  const bullets = [];
  let bulletIndex = 0;

  // Match * followed by space and text (bullet point pattern)
  // Pattern: * at start of line or after newline, followed by space and text
  // This pattern matches * anywhere when it's followed by space and text
  result = result.replace(/(^|\n)\s*\*\s+([^\n*]+)/g, (match, before, text) => {
    const placeholder = `${bulletPlaceholder}${bulletIndex}${bulletPlaceholder}`;
    bullets.push({ placeholder, bullet: `* ${text}` });
    bulletIndex++;
    return `${before}${placeholder}`;
  });

  // Also handle * at the very beginning of text (without newline before)
  // This is a fallback for cases where * is at the absolute start
  result = result.replace(/^(\s*)\*\s+([^\n*]+)/gm, (match, indent, text) => {
    // Check if this wasn't already captured by the previous pattern
    if (!match.includes(bulletPlaceholder)) {
      const placeholder = `${bulletPlaceholder}${bulletIndex}${bulletPlaceholder}`;
      bullets.push({ placeholder, bullet: `* ${text}` });
      bulletIndex++;
      return `${indent}${placeholder}`;
    }
    return match;
  });

  // Preserve Markdown code blocks (```code```) before processing
  const codeBlockPlaceholder = "__CODE_BLOCK__";
  const codeBlocks = [];
  let codeBlockIndex = 0;

  // Match code blocks: ```language\ncode\n```
  result = result.replace(/```([\s\S]*?)```/g, (match, code) => {
    const placeholder = `${codeBlockPlaceholder}${codeBlockIndex}${codeBlockPlaceholder}`;
    codeBlocks.push({ placeholder, code: match });
    codeBlockIndex++;
    return placeholder;
  });

  // Preserve inline code (`code`) before processing
  const inlineCodePlaceholder = "__INLINE_CODE__";
  const inlineCodes = [];
  let inlineCodeIndex = 0;

  // Match inline code: `code` (but not inside code blocks)
  result = result.replace(/`([^`\n]+)`/g, (match, code) => {
    const placeholder = `${inlineCodePlaceholder}${inlineCodeIndex}${inlineCodePlaceholder}`;
    inlineCodes.push({ placeholder, code: match });
    inlineCodeIndex++;
    return placeholder;
  });

  // Preserve Markdown tables before processing
  const tablePlaceholder = "__TABLE_ROW__";
  const tableRows = [];
  let tableIndex = 0;

  // Match table rows: lines starting with | and ending with | — but do NOT treat as table row
  // any line that contains a markdown link ]( or [COVER] so source/cover lines stay below the table.
  // When we skip such a line, strip the pipes and add a blank line before so it renders OUTSIDE the table.
  result = result.replace(/^\s*\|.+\|\s*$/gm, (match) => {
    const trimmed = match.trim();
    const isLinkOrCover = /\]\s*\(|\bCOVER\b|\[\s*C\s*OVER\s*\]/i.test(trimmed);
    if (isLinkOrCover) {
      const inner = trimmed
        .replace(/^\|\s*/, "")
        .replace(/\s*\|$/, "")
        .trim();
      return "\n\n" + inner + "\n";
    }
    const placeholder = `${tablePlaceholder}${tableIndex}${tablePlaceholder}`;
    tableRows.push({ placeholder, row: trimmed });
    tableIndex++;
    return placeholder;
  });

  // Same line: last table row glued to link (e.g. "| الرابع | أخرى | 5.1 |[بحث القوى...") — split so the row stays in table and link goes below
  result = result.replace(
    /(\|(?:[^|\n]+\|)+[^|\n]*\|)\s*(\[)/g,
    (_, row, bracket) => row + "\n\n" + bracket,
  );

  // Lines that start with | but do NOT end with | (e.g. streamed "| [link](url)<br>") — treat as link/cover outside table
  result = result.replace(
    /^\s*\|([^\n]*?(?:\]\s*\(|\bCOVER\b|\[\s*C\s*OVER\s*\])[^\n]*)$/gm,
    (match) => {
      const inner = match.replace(/^\s*\|\s*/, "").trim();
      return "\n\n" + inner + "\n";
    },
  );

  // Preserve Markdown links [text](url) before processing
  const linkPlaceholder = "__MARKDOWN_LINK__";
  const links = [];
  let linkIndex = 0;

  // Match Markdown links: [text](url) or [text] (url) — allow space between ] and (; fix "https: //" in URL
  result = result.replace(
    /\[([^\]]+)\]\s*\(\s*([^)]+)\s*\)/g,
    (match, linkText, url) => {
      // Clean up URL: remove spaces after colon (https: // -> https://) and all other spaces
      let cleanUrl = url.trim();
      // Fix "https: //" or "http: //" to "https://" or "http://"
      cleanUrl = cleanUrl.replace(/:\s*\/\s*\//g, "://");
      // Remove any remaining spaces
      cleanUrl = cleanUrl.replace(/\s+/g, "");
      const placeholder = `${linkPlaceholder}${linkIndex}${linkPlaceholder}`;
      links.push({ placeholder, linkText: linkText.trim(), url: cleanUrl });
      linkIndex++;
      return placeholder;
    },
  );

  // Preserve Markdown list items (bullet points: -, +, or numbered) before processing
  // Note: * bullet points are already preserved above, so we only match - and + here
  const listItemPlaceholder = "__LIST_ITEM__";
  const listItems = [];
  let listItemIndex = 0;

  // Match list items: - item, + item, or 1. item (but not * which is already preserved)
  // Also match * items that weren't captured by bullet preservation (shouldn't happen, but just in case)
  result = result.replace(
    /^(\s*)([-+]|\d+\.)\s+(.+)$/gm,
    (match, indent, marker, content) => {
      // Skip if this is a placeholder (already processed)
      if (match.includes(bulletPlaceholder)) {
        return match;
      }
      const placeholder = `${listItemPlaceholder}${listItemIndex}${listItemPlaceholder}`;
      listItems.push({ placeholder, marker, content, indent });
      listItemIndex++;
      return `${indent}${placeholder}`;
    },
  );

  // Preserve **bold** markers so addSpacesBetweenWords never inserts spaces inside them
  // (e.g. "**ملاحظات:**" must not become "**ملاحظات :**" which breaks CommonMark bold parsing)
  const boldPlaceholderPrefix = "__BOLD_MARKER__";
  const boldMarkers = [];
  let boldMarkerIndex = 0;
  result = result.replace(/\*\*[^*\n]+?\*\*/g, (match) => {
    const ph = `${boldPlaceholderPrefix}${boldMarkerIndex}${boldPlaceholderPrefix}`;
    boldMarkers.push({ ph, match });
    boldMarkerIndex++;
    return ph;
  });

  // Preserve newlines by temporarily replacing them
  const newlinePlaceholder = "__NEWLINE__";
  result = result.replace(/\n/g, newlinePlaceholder);

  // Then, add spaces between words, numbers, and punctuation
  result = addSpacesBetweenWords(result);

  // Normalize spacing around punctuation for a professional display
  result = result
    // Remove space before punctuation like : . ، % (but not decimal point)
    .replace(/\s+([:،%])/g, "$1")
    // Ensure single space after (:, ،) — but NOT when immediately followed by * (bold/italic
    // marker). Adding a space there produces "**text: **" which CommonMark refuses to render as bold.
    .replace(/([:،])[ \t]*(?!\*)/g, "$1 ")
    // Handle % - no space before, single space after
    .replace(/\s*%\s*/g, "% ");

  // Clean up multiple spaces but preserve newlines
  result = result.replace(/[ \t]+/g, " ");

  // Restore newlines
  result = result.replace(new RegExp(newlinePlaceholder, "g"), "\n");

  // Restore bold markers (before bullet/list restoration)
  boldMarkers.forEach(({ ph, match }) => {
    result = result.replace(ph, match);
  });

  // Restore bullet points first (before list items)
  bullets.forEach(({ placeholder, bullet }) => {
    result = result.replace(placeholder, bullet);
  });

  // Restore list items
  listItems.forEach(({ placeholder, marker, content, indent }) => {
    result = result.replace(placeholder, `${marker} ${content}`);
  });

  // Restore inline code
  inlineCodes.forEach(({ placeholder, code }) => {
    result = result.replace(placeholder, code);
  });

  // Restore code blocks
  codeBlocks.forEach(({ placeholder, code }) => {
    result = result.replace(placeholder, code);
  });

  // Restore table rows
  tableRows.forEach(({ placeholder, row }) => {
    result = result.replace(placeholder, row);
  });

  // Restore Markdown links - ensure URL is clean and valid
  links.forEach(({ placeholder, linkText, url }) => {
    // Final cleanup of URL to ensure it's valid
    let finalUrl = url.trim();
    // Fix common URL issues: "https: //" -> "https://", "http: //" -> "http://"
    finalUrl = finalUrl.replace(/:\s*\/\s*\//g, "://");
    // Remove any remaining spaces
    finalUrl = finalUrl.replace(/\s+/g, "");
    // Only add https:// to bare hostnames — NOT to relative paths (starting with /)
    // Adding https:// to a relative path produces a broken triple-slash URL (https:///path)
    if (!finalUrl.match(/^https?:\/\//) && !finalUrl.startsWith("/")) {
      finalUrl = `https://${finalUrl}`;
    }
    result = result.replace(placeholder, `[${linkText}](${finalUrl})`);
  });

  // Final bold normalization: after all restorations, trim spaces inside **...** markers.
  // Bold content inside list items is restored here (bypasses the earlier bold placeholder
  // step), so patterns like "** text **" must be normalized here to "**text**" so
  // CommonMark renders them as bold (left-flanking delimiters must not be followed by
  // whitespace per the CommonMark spec).
  result = result.replace(
    /\*\*[ \t]+([^*\n]+?)[ \t]*\*\*/g,
    (_, inner) => `**${inner.trim()}**`,
  );
  result = result.replace(
    /\*\*[ \t]*([^*\n]+?)[ \t]+\*\*/g,
    (_, inner) => `**${inner.trim()}**`,
  );

  // Ensure standalone bold lines (a line whose non-whitespace content is entirely **...**)
  // are preceded by a blank line so CommonMark renders them as their own paragraph.
  result = result.replace(/([^\n])\n(\*\*[^\n]+\*\*)(\s*$)/gm, "$1\n\n$2$3");

  // Horizontal rule (---) and headings: when stream sends "---###" with no newline, split so --- renders as <hr> and ### as heading
  result = result.replace(/---(\s*#{1,6}\s)/g, "---\n\n$1");

  // Normalize age ranges: "-15 19" / " -15 19" / "19 15-" → "15 - 19"
  // (1) minus before first number (with ^|>|\s|\n so line/cell start is covered)
  result = result.replace(
    /(^|>|\s|\n)-(\d+)\s+(\d+)(?=\s*<|\s|$|\)|\n)/g,
    "$1$2 - $3",
  );
  // (2) minus after second number (allow optional space before hyphen: "19 15 -")
  result = result.replace(
    /(\d+)\s+(\d+)\s*-(?=\s*<|\s|$|\)|\n)/g,
    (_, a, b) => {
      const n = parseInt(a, 10);
      const m = parseInt(b, 10);
      return n <= m ? `${n} - ${m}` : `${m} - ${n}`;
    },
  );

  return result;
};

// Default response content (same as defaultResponse) - Arabic and English
const DEFAULT_RESPONSE_CONTENT = {
  text_ar: `**حدث خطأ غير متوقع، يرجى المحاولة مرة أخرى والتواصل مع الدعم إذا استمرت المشكلة.**

أو يمكنك الرجوع إلى المصدر الرئيسي للبيانات: [https://capmas.gov.eg](https://capmas.gov.eg)`,
  text_en: `**An unexpected technical error occurred. Please try again and contact support if the problem persists.**

Or you can refer to the main data source: [https://capmas.gov.eg](https://capmas.gov.eg)`,
  suggestions_ar: [
    "سوق العمل والهجرة",
    "كيفية الحصول على بيانات سوق العمل",
    "الموقع الرسمي",
    "مصدر بيانات الجهاز",
  ],
  suggestions_en: [
    "Labor market and migration",
    "How to get labor market data",
    "Official website",
    "CAPMAS data source",
  ],
};

// Mock data structure – كل عنصر يستخدم نفس محتوى defaultResponse (عربي + إنجليزي)
const mockApiData = {
  مرحبا: {
    request: "مرحبا",
    response: {
      ...DEFAULT_RESPONSE_CONTENT,
      metadata: {
        confidence: 0.95,
        category: "greeting",
        timestamp: new Date().toISOString(),
      },
    },
  },
  "الخدمات الإحصائية": {
    request: "الخدمات الإحصائية",
    response: {
      ...DEFAULT_RESPONSE_CONTENT,
      metadata: {
        confidence: 0.92,
        category: "services",
        timestamp: new Date().toISOString(),
      },
    },
  },
  "البيانات الإحصائية": {
    request: "البيانات الإحصائية",
    response: {
      ...DEFAULT_RESPONSE_CONTENT,
      metadata: {
        confidence: 0.88,
        category: "data",
        timestamp: new Date().toISOString(),
      },
    },
  },
  التقارير: {
    request: "التقارير",
    response: {
      ...DEFAULT_RESPONSE_CONTENT,
      metadata: {
        confidence: 0.9,
        category: "reports",
        timestamp: new Date().toISOString(),
      },
    },
  },
  "الموقع الرسمي": {
    request: "الموقع الرسمي",
    response: {
      ...DEFAULT_RESPONSE_CONTENT,
      metadata: {
        confidence: 0.85,
        category: "website",
        timestamp: new Date().toISOString(),
      },
    },
  },
  "الخدمات الإلكترونية": {
    request: "الخدمات الإلكترونية",
    response: {
      ...DEFAULT_RESPONSE_CONTENT,
      metadata: {
        confidence: 0.87,
        category: "electronic",
        timestamp: new Date().toISOString(),
      },
    },
  },
  "المؤشرات الاقتصادية": {
    request: "المؤشرات الاقتصادية",
    response: {
      ...DEFAULT_RESPONSE_CONTENT,
      metadata: {
        confidence: 0.91,
        category: "indicators",
        timestamp: new Date().toISOString(),
      },
    },
  },
  "البيانات السكانية": {
    request: "البيانات السكانية",
    response: {
      ...DEFAULT_RESPONSE_CONTENT,
      metadata: {
        confidence: 0.89,
        category: "population",
        timestamp: new Date().toISOString(),
      },
    },
  },
};

// English keywords mapping
const englishKeywords = {
  hello: "مرحبا",
  hi: "مرحبا",
  services: "الخدمات الإحصائية",
  "statistical services": "الخدمات الإحصائية",
  data: "البيانات الإحصائية",
  "statistical data": "البيانات الإحصائية",
  reports: "التقارير",
  website: "الموقع الرسمي",
  "official website": "الموقع الرسمي",
  electronic: "الخدمات الإلكترونية",
  "electronic services": "الخدمات الإلكترونية",
  indicators: "المؤشرات الاقتصادية",
  "economic indicators": "المؤشرات الاقتصادية",
  population: "البيانات السكانية",
  "population data": "البيانات السكانية",
};

// Simulate API delay
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Find best match for user input
const findBestMatch = (userMessage) => {
  const message = userMessage.toLowerCase().trim();

  // Check English keywords first
  for (const [englishKey, arabicKey] of Object.entries(englishKeywords)) {
    if (message.includes(englishKey)) {
      return mockApiData[arabicKey];
    }
  }

  // Check Arabic keywords
  for (const [key, data] of Object.entries(mockApiData)) {
    if (
      message.includes(key.toLowerCase()) ||
      key.toLowerCase().includes(message)
    ) {
      return data;
    }
  }

  // Check partial matches
  for (const [key, data] of Object.entries(mockApiData)) {
    const keyWords = key.split(" ");
    const messageWords = message.split(" ");

    const hasMatch = keyWords.some((keyWord) =>
      messageWords.some(
        (msgWord) => keyWord.includes(msgWord) || msgWord.includes(keyWord),
      ),
    );

    if (hasMatch) {
      return data;
    }
  }

  return null;
};

// Real API call with streaming or JSON response
const callRealAPI = async (
  userMessage,
  chatId, // Chat ID to generate unique conv_id for each conversation
  abortSignal,
  onChunk,
  onInfo,
  onComplete,
  onError,
  token = null, // Authorization token
  userId = null, // User ID
  convId = null, // conv_id from chatHistory (if exists), otherwise null
  onEventChange = null, // v1 only: called when SSE event type changes (e.g. Final_Answer -> labour_migration_agent)
) => {
  try {
    // Validate CHATBOT_API_BASE_URL
    if (!END_POINTS.CHATBOT_API_BASE_URL || typeof END_POINTS.CHATBOT_API_BASE_URL !== "string") {
      throw new Error(
        "Invalid CHATBOT_API_BASE_URL: CHATBOT_API_BASE_URL must be a valid string",
      );
    }

    // Validate userMessage
    if (!userMessage || typeof userMessage !== "string") {
      throw new Error(
        "Invalid userMessage: userMessage must be a valid string",
      );
    }

    // Clean and normalize CHATBOT_API_BASE_URL (remove trailing slashes)
    const cleanApiBaseUrl = END_POINTS.CHATBOT_API_BASE_URL.trim().replace(/\/+$/, "");

    let endpoint, requestBody;

    // أول سؤال في المحادثة: conv_id = null. بعد ذلك نستخدم القيمة المرتجعة في هيدر x-conversation-id للأسئلة التالية في نفس الشات.
    if (API_VERSION === "v2") {
      // endpoint = `${END_POINTS.CHATBOT_API_BASE_URL}v2/query`;
      endpoint = `${cleanApiBaseUrl}v2/api/messages`;
      // requestBody = { query: userMessage };
      requestBody = {
        query: userMessage,
        conv_id: convId ?? null, // أول طلب: null. الطلبات التالية: من x-conversation-id
      };
    } else {
      endpoint = `${cleanApiBaseUrl}/api/messages`;
      requestBody = {
        query: userMessage,
        conv_id: convId ?? null, // أول طلب: null. الطلبات التالية: من x-conversation-id
      };
    }

    // Clean endpoint (remove any extra spaces or invalid characters)
    endpoint = endpoint.trim();

    // Validate endpoint
    if (!endpoint || typeof endpoint !== "string" || endpoint === "") {
      throw new Error(
        `Invalid endpoint: endpoint must be a valid non-empty string. Got: ${endpoint}`,
      );
    }

    // Validate endpoint is a valid URL
    try {
      new URL(endpoint);
    } catch (error) {
      throw new Error(
        `Invalid endpoint URL: ${endpoint}. Must be a valid HTTP/HTTPS URL. Error: ${error.message}`,
      );
    }

    // Validate requestBody
    if (!requestBody || typeof requestBody !== "object") {
      throw new Error(
        "Invalid requestBody: requestBody must be a valid object",
      );
    }

    // Prepare headers - ensure all values are strings
    const headers = {
      "Content-Type": "application/json",
    };

    // Add authorization header if token is provided
    if (token != null && token !== "") {
      // Ensure token is a string
      const tokenString = String(token).trim();
      if (tokenString) {
        headers["authorization"] = tokenString;
      }
    }

    // Add userId header if userId is provided
    if (userId != null && userId !== "") {
      const userIdString = String(userId).trim();
      if (userIdString) {
        headers["userId"] = userIdString;
      }
    }

    // Validate headers - ensure all values are strings
    for (const [key, value] of Object.entries(headers)) {
      if (typeof value !== "string") {
        throw new Error(
          `Invalid header value for "${key}": must be a string. Got: ${typeof value}`,
        );
      }
      if (value === "") {
        throw new Error(`Invalid header value for "${key}": cannot be empty`);
      }
    }

    // Validate and stringify requestBody
    let requestBodyString;
    try {
      requestBodyString = JSON.stringify(requestBody);
      // Validate that stringify produced a valid string
      if (typeof requestBodyString !== "string") {
        throw new Error(
          `JSON.stringify returned non-string value: ${typeof requestBodyString}`,
        );
      }
    } catch (error) {
      throw new Error(
        `Failed to stringify requestBody: ${
          error.message
        }. RequestBody: ${JSON.stringify(requestBody)}`,
      );
    }

    // Prepare fetch options - ensure all values are valid
    const fetchOptions = {
      method: "POST",
      headers: headers,
      body: requestBodyString,
    };

    // Validate fetchOptions
    if (typeof fetchOptions.method !== "string") {
      throw new Error(
        `Invalid fetch method: must be a string. Got: ${typeof fetchOptions.method}`,
      );
    }
    if (!(fetchOptions.headers instanceof Object)) {
      throw new Error(
        `Invalid fetch headers: must be an object. Got: ${typeof fetchOptions.headers}`,
      );
    }
    if (typeof fetchOptions.body !== "string") {
      throw new Error(
        `Invalid fetch body: must be a string. Got: ${typeof fetchOptions.body}`,
      );
    }

    // Only add signal if it's a valid AbortSignal
    if (abortSignal) {
      if (abortSignal instanceof AbortSignal) {
        fetchOptions.signal = abortSignal;
      } else {
        console.warn(
          "Invalid abortSignal provided, ignoring it. Expected AbortSignal, got:",
          typeof abortSignal,
        );
      }
    }

    // Final validation before fetch - endpoint is already validated with new URL() above
    // Double-check that endpoint is a valid string
    if (typeof endpoint !== "string") {
      throw new Error(
        `Invalid endpoint type: expected string, got ${typeof endpoint}`,
      );
    }

    // Final validation - ensure fetchOptions is clean
    // Remove any undefined or null values from fetchOptions
    const cleanFetchOptions = {};

    if (fetchOptions.method) {
      cleanFetchOptions.method = fetchOptions.method;
    }

    if (fetchOptions.headers && typeof fetchOptions.headers === "object") {
      // Clean headers - remove any undefined/null values
      cleanFetchOptions.headers = {};
      for (const [key, value] of Object.entries(fetchOptions.headers)) {
        if (value != null && value !== "") {
          cleanFetchOptions.headers[key] = value;
        }
      }
    }

    if (fetchOptions.body != null) {
      cleanFetchOptions.body = fetchOptions.body;
    }

    if (
      fetchOptions.signal != null &&
      fetchOptions.signal instanceof AbortSignal
    ) {
      cleanFetchOptions.signal = fetchOptions.signal;
    }

    const response = await fetch(endpoint, cleanFetchOptions);

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    // استخراج conv_id و messageId من هيدر الاستجابة (قد يكونان null في الاستجابة المُجزّأة لأن الـ backend يرسلهما أحياناً في الـ trailers بعد انتهاء الـ stream؛ نُحدّثهما لاحقاً من response.trailers أو من محتوى الـ stream "details")
    const conversationIdFromHeader =
      response.headers.get("x-conversation-id") ||
      response.headers.get("X-Conversation-Id") ||
      response.headers.get("X-Conversation-ID") ||
      response.headers.get("X-CONVERSATION-ID");
    const messageIdFromHeader =
      response.headers.get("x-message-id") ||
      response.headers.get("X-Message-Id") ||
      response.headers.get("X-Message-ID") ||
      response.headers.get("X-MESSAGE-ID");

    // Handle as streaming response
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullResponse = "";
    let buffer = "";
    let lastChunkWasBullet = false; // Track if last chunk was a bullet marker
    let graphData = null; // Store graph data if received (parsed only after stream ends)
    let graphBuffer = ""; // Accumulate lines that look like graph JSON during stream; do not add to response text
    let inGraphBlock = false; // True while we're inside a graph JSON block (parse only after stream ends)
    let excelCodeBuffer = ""; // v1: accumulate generate_excel event data (Python/code); do not add to response text
    let convIdFromResponse = conversationIdFromHeader
      ? String(conversationIdFromHeader).trim() || null
      : null; // conv_id من هيدر x-conversation-id للاستخدام في الطلبات التالية
    if (convIdFromResponse && chatId)
      conversationIdByChatId.set(chatId, convIdFromResponse); // تخزين فوراً للطلبات التالية (fallback عند عدم ظهور الهيدر لاحقاً)
    let messageIdFromResponse = messageIdFromHeader || null; // Store messageId from API response (prioritize header)
    let canExportFromStream = undefined; // From stream: {"status":"details","data":{"messageId":"...","canExport":true/false}}
    // v1: current event type (e.g. Final_Answer, labour_migration_agent, generate_excel).
    // generate_excel / generate_graphs: ChatBot shows title-only event strips (no inner body; chart & Excel in answer).
    let currentSSEEvent = null;

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;

      // Process complete lines
      const lines = buffer.split("\n");
      buffer = lines.pop() || ""; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.trim() === "") continue;

        // v1 format: "event: X" then "data: Y"
        // Notify on every event (including first) so all events get a box; only Final_Answer is shown outside boxes
        if (API_VERSION === "v1") {
          const eventName = parseSSEEvent(line);
          if (eventName != null) {
            const isSwitch = currentSSEEvent !== eventName; // true for first event (null -> X) or when event changes
            currentSSEEvent = eventName;
            if (isSwitch && typeof onEventChange === "function") {
              try {
                onEventChange(eventName);
              } catch (e) {
                console.warn("onEventChange error:", e);
              }
            }
            continue; // event line consumed, do not treat as data
          }
        }

        // If we're accumulating graph JSON, add this line and try to parse when complete
        if (inGraphBlock) {
          graphBuffer += (graphBuffer ? "\n" : "") + line;
          try {
            const parsed = JSON.parse(
              graphBuffer.replace(/^data:\s*/i, "").trim(),
            );
            if (parsed && typeof parsed.graph === "string") {
              graphData = parsed.graph;
              graphBuffer = "";
              inGraphBlock = false;
            }
          } catch (_) {}
          continue;
        }

        // Start of graph JSON: do not add to response text, buffer for parse at end
        if (
          line.trim().includes('"graph"') ||
          line.trim().includes("'graph'")
        ) {
          inGraphBlock = true;
          graphBuffer = line;
          try {
            const normalized = line
              .replace(/^data:\s*/i, "")
              .trim()
              .replace(/'/g, '"');
            const parsed = JSON.parse(normalized);
            if (parsed && typeof parsed.graph === "string") {
              graphData = parsed.graph;
              graphBuffer = "";
              inGraphBlock = false;
            }
          } catch (_) {}
          continue;
        }

        // Try to parse as JSON first (for info messages)
        if (line.trim().startsWith("{") || line.trim().startsWith("'")) {
          try {
            // Handle both single and double quotes in JSON
            const normalizedLine = line.trim().replace(/'/g, '"');
            const jsonData = JSON.parse(normalizedLine);

            // Check if it's an info message - show to user (e.g. جاري تحليل السؤال، جاري البحث في البيانات)
            if (jsonData.status === "info" && jsonData.message) {
              onInfo(jsonData.message);
              continue; // Skip adding to response text
            }

            // Don't show error objects in message content (e.g. {"type": "error", "message": "'clarification_message'"})
            if (jsonData.type === "error" && jsonData.message !== undefined) {
              continue; // Skip adding to response text
            }

            // Check if it contains graph data
            if (jsonData.graph) {
              graphData = jsonData.graph;
              continue; // Skip adding to response
            }

            // Details payload: messageId + canExport (+ conv_id كـ fallback إذا لم يأتِ في الهيدر)
            if (
              jsonData.status === "details" &&
              jsonData.data &&
              typeof jsonData.data === "object"
            ) {
              if (jsonData.data.messageId != null)
                messageIdFromResponse = jsonData.data.messageId;
              if (jsonData.data.canExport !== undefined)
                canExportFromStream =
                  jsonData.data.canExport === true ||
                  jsonData.data.canExport === "true";
              if (jsonData.data.conv_id != null && convIdFromResponse == null) {
                convIdFromResponse =
                  String(jsonData.data.conv_id).trim() || null;
                if (convIdFromResponse && chatId)
                  conversationIdByChatId.set(chatId, convIdFromResponse);
              }
              continue;
            }

            // If it's valid JSON but not info or graph, skip it (don't add to response)
            continue;
          } catch (e) {
            // If JSON parsing fails but looks like JSON or error payload, skip it
            const trimmed = line.trim();
            if (
              trimmed.includes("'status'") ||
              trimmed.includes('"status"') ||
              trimmed.includes("'graph'") ||
              trimmed.includes('"graph"') ||
              (trimmed.includes("'type'") && trimmed.includes("'error'")) ||
              (trimmed.includes('"type"') && trimmed.includes('"error"'))
            ) {
              continue;
            }
            // Otherwise treat as regular text
          }
        }

        // Handle SSE format
        const data = parseSSEData(line);
        if (data !== null) {
          // v1: accumulate generate_excel event in buffer; do not add to response text (same as graph)
          if (API_VERSION === "v1" && currentSSEEvent === "generate_excel") {
            excelCodeBuffer += data;
            continue;
          }
          // Try to parse SSE data as JSON
          if (data.trim().startsWith("{") || data.trim().startsWith("'")) {
            try {
              // Handle both single and double quotes in JSON
              const normalizedData = data.trim().replace(/'/g, '"');
              const jsonData = JSON.parse(normalizedData);

              // Check if it's an info message - show to user
              if (jsonData.status === "info" && jsonData.message) {
                onInfo(jsonData.message);
                continue; // Skip adding to response text
              }
              // Don't show error objects in message content (e.g. {"type": "error", "message": "'clarification_message'"})
              if (jsonData.type === "error" && jsonData.message !== undefined) {
                continue; // Skip adding to response text
              }
              // Check if it contains graph data - extract and skip adding to response text
              if (jsonData.graph && typeof jsonData.graph === "string") {
                graphData = jsonData.graph;
                continue;
              }
              // Details payload: messageId + canExport (+ conv_id كـ fallback)
              if (
                jsonData.status === "details" &&
                jsonData.data &&
                typeof jsonData.data === "object"
              ) {
                if (jsonData.data.messageId != null)
                  messageIdFromResponse = jsonData.data.messageId;
                if (jsonData.data.canExport !== undefined)
                  canExportFromStream =
                    jsonData.data.canExport === true ||
                    jsonData.data.canExport === "true";
                if (
                  jsonData.data.conv_id != null &&
                  convIdFromResponse == null
                ) {
                  convIdFromResponse =
                    String(jsonData.data.conv_id).trim() || null;
                  if (convIdFromResponse && chatId)
                    conversationIdByChatId.set(chatId, convIdFromResponse);
                }
                continue;
              }
              // If it's valid JSON but not info or graph, skip it
              continue;
            } catch (e) {
              // If JSON parsing fails but looks like JSON or error payload, skip it
              const dataTrimmed = data.trim();
              if (
                dataTrimmed.includes("'status'") ||
                dataTrimmed.includes('"status"') ||
                dataTrimmed.includes("'graph'") ||
                dataTrimmed.includes('"graph"') ||
                (dataTrimmed.includes("'type'") &&
                  dataTrimmed.includes("'error'")) ||
                (dataTrimmed.includes('"type"') &&
                  dataTrimmed.includes('"error"'))
              ) {
                continue;
              }
              // Otherwise treat as regular text
            }
          }

          if (data === "[DONE]") {
            // End of stream
            break;
          }

          // Handle empty data (space or newline) - preserve it
          if (data === " " || data === "\n" || data.trim() === "") {
            // If we just processed a bullet marker, the space is part of the bullet formatting
            if (lastChunkWasBullet) {
              // Space after bullet is already handled in "* ", just continue
              continue;
            }
            // Otherwise, preserve the space/newline (v1: only Final_Answer goes into response text)
            if (API_VERSION !== "v1" || currentSSEEvent === "Final_Answer")
              fullResponse += data;
            onChunk(data);
            continue;
          }

          // Handle single * as bullet point marker - keep * as is
          // If data is just "*" or "* " (with optional space), it's a bullet marker
          if (data.trim() === "*" || data.trim() === "* ") {
            // Keep * as is, don't add newline - only preserve what's in the response
            const processedData = "* ";
            if (API_VERSION !== "v1" || currentSSEEvent === "Final_Answer")
              fullResponse += processedData;
            onChunk(processedData);
            lastChunkWasBullet = true;
          } else if (lastChunkWasBullet) {
            // If we have text after a bullet marker, add it directly after "* "
            // Don't process it through processSSEContent to avoid breaking the bullet point
            if (API_VERSION !== "v1" || currentSSEEvent === "Final_Answer")
              fullResponse += data;
            onChunk(data);
            lastChunkWasBullet = false;
          } else {
            // Normal text processing (v1: only Final_Answer goes into response text)
            const processedData = processSSEContent(data);
            if (API_VERSION !== "v1" || currentSSEEvent === "Final_Answer")
              fullResponse += processedData;
            onChunk(processedData);
            lastChunkWasBullet = false;
          }
        } else if (
          line.trim() !== "" &&
          !line.trim().startsWith("{") &&
          !line.trim().startsWith("'")
        ) {
          // Regular text line (v1: only Final_Answer goes into response text)
          const cleanedLine = parseJSONString(line);
          const processedLine = processSSEContent(cleanedLine);
          if (API_VERSION !== "v1" || currentSSEEvent === "Final_Answer")
            fullResponse += processedLine + " ";
          onChunk(processedLine + " ");
        }
      }
    }

    // Process any remaining data in buffer
    if (buffer.trim()) {
      if (buffer.trim().startsWith("{") || buffer.trim().startsWith("'")) {
        try {
          // Handle both single and double quotes in JSON
          const normalizedBuffer = buffer.trim().replace(/'/g, '"');
          const jsonData = JSON.parse(normalizedBuffer);
          if (jsonData.status === "info" && jsonData.message) {
            onInfo(jsonData.message);
            // Don't add to response
          }
          // Check if it contains graph data
          if (jsonData.graph) {
            graphData = jsonData.graph;
          }
          // Details payload: messageId + canExport
          if (
            jsonData.status === "details" &&
            jsonData.data &&
            typeof jsonData.data === "object"
          ) {
            if (jsonData.data.messageId != null)
              messageIdFromResponse = jsonData.data.messageId;
            if (jsonData.data.canExport !== undefined)
              canExportFromStream =
                jsonData.data.canExport === true ||
                jsonData.data.canExport === "true";
          }
          // If it's valid JSON but not info or graph, don't add to response
        } catch (e) {
          // If JSON parsing fails but looks like JSON, skip it
          if (
            !buffer.trim().includes("'status'") &&
            !buffer.trim().includes('"status"') &&
            !buffer.trim().includes("'graph'") &&
            !buffer.trim().includes('"graph"')
          ) {
            // Only add to response if it doesn't look like JSON status message
            const cleanedBuffer = parseJSONString(buffer);
            const processedBuffer = processSSEContent(cleanedBuffer);
            if (API_VERSION !== "v1" || currentSSEEvent === "Final_Answer")
              fullResponse += processedBuffer;
            onChunk(processedBuffer);
          }
        }
      } else {
        const data = parseSSEData(buffer);
        if (data !== null && data !== "[DONE]" && data.trim() !== "") {
          // Check if SSE data is JSON
          if (data.trim().startsWith("{") || data.trim().startsWith("'")) {
            try {
              const normalizedData = data.trim().replace(/'/g, '"');
              const jsonData = JSON.parse(normalizedData);
              if (jsonData.status === "info" && jsonData.message) {
                onInfo(jsonData.message);
              }
              // Check if it contains graph data
              if (jsonData.graph) {
                graphData = jsonData.graph;
              }
              // Details payload: messageId + canExport (e.g. last chunk with no newline)
              if (
                jsonData.status === "details" &&
                jsonData.data &&
                typeof jsonData.data === "object"
              ) {
                if (jsonData.data.messageId != null)
                  messageIdFromResponse = jsonData.data.messageId;
                if (jsonData.data.canExport !== undefined)
                  canExportFromStream =
                    jsonData.data.canExport === true ||
                    jsonData.data.canExport === "true";
              }
              // If it's valid JSON but not info or graph, don't add to response
            } catch (e) {
              // If parsing fails but doesn't look like status message, add to response
              if (
                !data.trim().includes("'status'") &&
                !data.trim().includes('"status"') &&
                !data.trim().includes("'graph'") &&
                !data.trim().includes('"graph"')
              ) {
                const cleanedData = parseJSONString(data);
                const processedData = processSSEContent(cleanedData);
                if (API_VERSION !== "v1" || currentSSEEvent === "Final_Answer")
                  fullResponse += processedData;
                onChunk(processedData);
              }
            }
          } else {
            // Regular text data (v1: only Final_Answer goes into response text)
            const cleanedData = parseJSONString(data);
            const processedData = processSSEContent(cleanedData);
            if (API_VERSION !== "v1" || currentSSEEvent === "Final_Answer")
              fullResponse += processedData;
            onChunk(processedData);
          }
        } else if (
          buffer.trim() !== "" &&
          !buffer.trim().startsWith("{") &&
          !buffer.trim().startsWith("'")
        ) {
          const cleanedBuffer = parseJSONString(buffer);
          const processedBuffer = processSSEContent(cleanedBuffer);
          if (API_VERSION !== "v1" || currentSSEEvent === "Final_Answer")
            fullResponse += processedBuffer;
          onChunk(processedBuffer);
        }
      }
    }

    // بعد انتهاء الـ stream قد يرسل الـ backend قيم x-conversation-id و x-message-id في الـ trailers (هيدرات تُرسل بعد الـ body).
    // في المتصفح لا يدعم Fetch قراءة الـ trailers بشكل موحّد؛ نجرّب إن وُجدت ثم نعتمد على القيم من الـ stream (details) أو الـ fallback.
    try {
      if (
        response.trailers != null &&
        typeof response.trailers.then === "function"
      ) {
        const trailers = await response.trailers;
        if (trailers) {
          const tid =
            trailers.get("x-conversation-id") ||
            trailers.get("X-Conversation-Id") ||
            trailers.get("X-Conversation-ID") ||
            trailers.get("X-CONVERSATION-ID");
          const mid =
            trailers.get("x-message-id") ||
            trailers.get("X-Message-Id") ||
            trailers.get("X-Message-ID") ||
            trailers.get("X-MESSAGE-ID");
          if (tid != null && String(tid).trim()) {
            convIdFromResponse = String(tid).trim();
            if (chatId) conversationIdByChatId.set(chatId, convIdFromResponse);
          }
          if (mid != null && String(mid).trim())
            messageIdFromResponse = String(mid).trim();
        }
      }
    } catch (_) {
      // تجاهل إن لم تدعم البيئة response.trailers
    }

    // If we were accumulating graph JSON, append remaining buffer to graphBuffer
    // so we don't add the tail of the graph (e.g. "}") to fullResponse
    if (inGraphBlock && buffer.trim()) {
      graphBuffer += (graphBuffer ? "\n" : "") + buffer;
      buffer = "";
    }

    // Extract graph data from text if present (Python dict format: {'graph': '...'})
    // Also check if graph data is in a JSON object at the end of the stream (or in graphBuffer)
    let cleanedResponse = fullResponse.trimStart();

    // Combine buffer and graphBuffer with cleanedResponse to check for JSON objects
    let combinedResponse = (
      cleanedResponse +
      "\n" +
      buffer +
      (graphBuffer.trim() ? "\n" + graphBuffer : "")
    ).trim();

    // Strip optional "data: " prefix (SSE format) so we can find JSON object
    const dataPrefixMatch = combinedResponse.match(/^data:\s*/i);
    if (
      dataPrefixMatch &&
      combinedResponse
        .substring(dataPrefixMatch[0].length)
        .trim()
        .startsWith("{")
    ) {
      combinedResponse = combinedResponse
        .substring(dataPrefixMatch[0].length)
        .trim();
    }

    // Try to find and extract JSON object containing graph data
    // Look for JSON objects that might span multiple lines
    // Find the last complete JSON object in the response
    if (!graphData && combinedResponse.includes('"graph"')) {
      try {
        // Find the last opening brace that starts a JSON object containing "graph"
        let lastBraceIndex = combinedResponse.lastIndexOf("{");
        while (lastBraceIndex >= 0) {
          const jsonCandidate = combinedResponse.substring(lastBraceIndex);

          // Find matching closing brace, respecting double-quoted strings and escapes
          let braceCount = 0;
          let endIndex = -1;
          let inString = false;
          let escapeNext = false;
          for (let i = 0; i < jsonCandidate.length; i++) {
            const char = jsonCandidate[i];
            if (escapeNext) {
              escapeNext = false;
              continue;
            }
            if (char === "\\" && inString) {
              escapeNext = true;
              continue;
            }
            if (char === '"') {
              inString = !inString;
              continue;
            }
            if (!inString) {
              if (char === "{") braceCount++;
              if (char === "}") {
                braceCount--;
                if (braceCount === 0) {
                  endIndex = i + 1;
                  break;
                }
              }
            }
          }

          if (endIndex !== -1) {
            const jsonString = jsonCandidate.substring(0, endIndex);

            // Check if it contains "graph" key
            if (jsonString.includes('"graph"')) {
              try {
                // Try to parse as JSON
                const jsonData = JSON.parse(jsonString);
                if (jsonData.graph && typeof jsonData.graph === "string") {
                  graphData = jsonData.graph;
                  // Remove the JSON object from cleanedResponse (and optional "data: " prefix)
                  const jsonToRemove = combinedResponse.substring(
                    lastBraceIndex,
                    lastBraceIndex + endIndex,
                  );
                  cleanedResponse = cleanedResponse
                    .replace(jsonToRemove, "")
                    .replace(/^data:\s*/i, "")
                    .trim();
                  buffer = buffer.replace(jsonToRemove, "").trim();
                }
              } catch (parseError) {
                // If parsing fails, try to extract graph value directly using regex
                // Match: "graph": "..." where ... can contain escaped characters
                const graphMatch = jsonString.match(
                  /"graph"\s*:\s*"((?:[^"\\]|\\.)*)"/,
                );
                if (graphMatch && graphMatch[1]) {
                  // Unescape the string
                  graphData = graphMatch[1]
                    .replace(/\\n/g, "\n")
                    .replace(/\\r/g, "\r")
                    .replace(/\\t/g, "\t")
                    .replace(/\\"/g, '"')
                    .replace(/\\'/g, "'")
                    .replace(/\\\\/g, "\\");
                  // Remove the JSON object from cleanedResponse (and optional "data: " prefix)
                  const jsonToRemove = combinedResponse.substring(
                    lastBraceIndex,
                    lastBraceIndex + endIndex,
                  );
                  cleanedResponse = cleanedResponse
                    .replace(jsonToRemove, "")
                    .replace(/^data:\s*/i, "")
                    .trim();
                  buffer = buffer.replace(jsonToRemove, "").trim();
                }
              }
            }
          }

          if (graphData) break;
          lastBraceIndex = combinedResponse.lastIndexOf(
            "{",
            lastBraceIndex - 1,
          );
        }
      } catch (error) {
        // Ignore errors and continue with existing graph extraction logic
      }
    }

    // Extract links and links_cover data
    let linksData = null;
    let linksCoverData = null;

    // First, convert <br> to newlines (same replaceBrTags so stream-split "< br>" is handled)
    let processedResponse = replaceBrTags(cleanedResponse);

    // Look for links: {...} (with possible newlines/br tags)
    const linksPattern = /links:\s*\{([\s\S]*?)\}/;
    const linksMatch = processedResponse.match(linksPattern);
    if (linksMatch) {
      try {
        const linksStr = "{" + linksMatch[1] + "}";
        // Convert Python-style dict to JSON (handle single quotes and newlines)
        const linksJson = linksStr.replace(/'/g, '"').replace(/\n/g, "");
        linksData = JSON.parse(linksJson);

        // Fix broken URLs in links
        Object.keys(linksData).forEach((key) => {
          if (linksData[key]) {
            linksData[key] = linksData[key]
              .replace(/https:\s+\/\//g, "https://")
              .replace(/http:\s+\/\//g, "http://")
              .replace(/:\s+(\d+)/g, ":$1"); // Fix port numbers like ": 8080" -> ":8080"
          }
        });

        // Remove from both versions
        cleanedResponse = cleanedResponse
          .replace(/links:\s*\{[\s\S]*?\}/g, "")
          .trim();
        processedResponse = processedResponse.replace(linksPattern, "").trim();
      } catch (e) {
        console.error("Failed to parse links:", e);
      }
    }

    // Look for links_cover: {...} (with possible newlines/br tags)
    const linksCoverPattern = /links_cover:\s*\{([\s\S]*?)\}/;
    const linksCoverMatch = processedResponse.match(linksCoverPattern);
    if (linksCoverMatch) {
      try {
        const linksCoverStr = "{" + linksCoverMatch[1] + "}";
        // Convert Python-style dict to JSON (handle single quotes and newlines)
        const linksCoverJson = linksCoverStr
          .replace(/'/g, '"')
          .replace(/\n/g, "");
        linksCoverData = JSON.parse(linksCoverJson);

        // Fix broken URLs in links_cover
        Object.keys(linksCoverData).forEach((key) => {
          if (linksCoverData[key]) {
            linksCoverData[key] = linksCoverData[key]
              .replace(/https:\s+\/\//g, "https://")
              .replace(/http:\s+\/\//g, "http://")
              .replace(/:\s+(\d+)/g, ":$1"); // Fix port numbers like ": 8080" -> ":8080"
          }
        });

        // Remove from both versions
        cleanedResponse = cleanedResponse
          .replace(/links_cover:\s*\{[\s\S]*?\}/g, "")
          .trim();
        processedResponse = processedResponse
          .replace(linksCoverPattern, "")
          .trim();
      } catch (e) {
        console.error("Failed to parse links_cover:", e);
      }
    }

    // Use same <br>→newline so stream-split "< br>" is handled
    cleanedResponse = replaceBrTags(cleanedResponse);

    // Look for graph dictionary at the end of the response
    const graphStartPattern = "{'graph': '";
    const graphStartIndex = cleanedResponse.lastIndexOf(graphStartPattern);

    if (graphStartIndex !== -1) {
      // Extract everything from the graph start to the end
      const graphDictString = cleanedResponse.substring(graphStartIndex);

      // The graph dict should end with '} at the very end
      if (graphDictString.endsWith("'}")) {
        // Extract the graph HTML content (between {'graph': ' and '})
        const graphStart = graphStartPattern.length;
        const graphContent = graphDictString.substring(
          graphStart,
          graphDictString.length - 2,
        );

        // Decode escaped characters in proper order
        graphData = graphContent
          .replace(/\\n/g, "\n") // Replace escaped newlines
          .replace(/\\r/g, "\r") // Replace escaped carriage returns
          .replace(/\\t/g, "\t") // Replace escaped tabs
          .replace(/\\"/g, '"') // Replace escaped double quotes
          .replace(/\\'/g, "'") // Replace escaped single quotes
          .replace(/\\\\/g, "\\") // Replace escaped backslashes (must be last)
          .replace(/https:\s+\/\//g, "https://") // Fix broken URLs like "https: //"
          .replace(/http:\s+\/\//g, "http://") // Fix broken URLs like "http: //"
          .replace(/:\s+(\d+)/g, ":$1"); // Fix port numbers like ": 8080" -> ":8080"

        // Remove the graph dict from the displayed text
        cleanedResponse = cleanedResponse.substring(0, graphStartIndex).trim();
      } else {
        console.log("❌ Graph dict does not end with '} - skipping extraction");
      }
    } else {
      console.log("No graph pattern found in response");
    }

    // Final fallback: if canExport was not set from stream, search raw content for details JSON
    const rawForDetails =
      fullResponse +
      "\n" +
      buffer +
      (graphBuffer.trim() ? "\n" + graphBuffer : "");
    if (canExportFromStream === undefined) {
      if (
        /"status"\s*:\s*"details"[\s\S]*?"canExport"\s*:\s*true/.test(
          rawForDetails,
        ) ||
        /"canExport"\s*:\s*true[\s\S]*?"status"\s*:\s*"details"/.test(
          rawForDetails,
        )
      ) {
        canExportFromStream = true;
      }
    }

    // استخراج conv_id و messageId من آخر كائن "details" في الـ stream إن لم نستلمهما من الهيدرات/الـ trailers (الـ backend قد يرسلهما في نهاية الـ stream فقط)
    if (convIdFromResponse == null || messageIdFromResponse == null) {
      const detailsMatches = rawForDetails.matchAll(
        /"status"\s*:\s*"details"[\s\S]*?"data"\s*:\s*\{([^}]*)\}/g,
      );
      for (const m of detailsMatches) {
        const dataBlock = m[1];
        if (
          convIdFromResponse == null &&
          /"conv_id"\s*:\s*["']?([^"',}\s]+)/.test(dataBlock)
        ) {
          const idMatch = dataBlock.match(/"conv_id"\s*:\s*["']?([^"',}\s]+)/);
          if (idMatch && idMatch[1]) {
            convIdFromResponse = String(idMatch[1]).trim();
            if (chatId) conversationIdByChatId.set(chatId, convIdFromResponse);
          }
        }
        if (
          messageIdFromResponse == null &&
          /"messageId"\s*:\s*["']([^"']+)["']/.test(dataBlock)
        ) {
          const midMatch = dataBlock.match(
            /"messageId"\s*:\s*["']([^"']+)["']/,
          );
          if (midMatch && midMatch[1]) messageIdFromResponse = midMatch[1];
        }
      }
      // نفس الفكرة مع صيغة single-quote من الـ backend
      if (convIdFromResponse == null || messageIdFromResponse == null) {
        const detailsSingle = rawForDetails.matchAll(
          /'status'\s*:\s*'details'[\s\S]*?'data'\s*:\s*\{([^}]*)\}/g,
        );
        for (const m of detailsSingle) {
          const dataBlock = m[1];
          if (
            convIdFromResponse == null &&
            /'conv_id'\s*:\s*['"]?([^'",}\s]+)/.test(dataBlock)
          ) {
            const idMatch = dataBlock.match(
              /'conv_id'\s*:\s*['"]?([^'",}\s]+)/,
            );
            if (idMatch && idMatch[1]) {
              convIdFromResponse = String(idMatch[1]).trim();
              if (chatId)
                conversationIdByChatId.set(chatId, convIdFromResponse);
            }
          }
          if (
            messageIdFromResponse == null &&
            /'messageId'\s*:\s*['"]([^'"]+)['"]/.test(dataBlock)
          ) {
            const midMatch = dataBlock.match(
              /'messageId'\s*:\s*['"]([^'"]+)['"]/,
            );
            if (midMatch && midMatch[1]) messageIdFromResponse = midMatch[1];
          }
        }
      }
    }

    // v1: normalize excelCode from generate_excel event (may be JSON with "python"/"code" key or raw script)
    let excelCode = null;
    if (excelCodeBuffer && excelCodeBuffer.trim()) {
      const trimmed = excelCodeBuffer.trim();
      try {
        const parsed =
          typeof trimmed === "string" && trimmed.startsWith("{")
            ? JSON.parse(trimmed.replace(/'/g, '"'))
            : null;
        if (parsed && (parsed.python != null || parsed.code != null))
          excelCode =
            typeof parsed.python === "string"
              ? parsed.python
              : String(parsed.code ?? "");
        else excelCode = trimmed;
      } catch (_) {
        excelCode = trimmed;
      }
      if (!excelCode || !excelCode.trim()) excelCode = null;
    }

    // إذا لم يَرجع الـ backend أو الهيدر conv_id (مثلاً CORS)، نستخدم الـ conv_id الذي أرسلناه في الطلب حتى لا نفقد ربط المحادثة
    if (
      convIdFromResponse == null &&
      convId != null &&
      String(convId).trim() !== ""
    )
      convIdFromResponse = String(convId).trim();
    if (convIdFromResponse && chatId)
      conversationIdByChatId.set(chatId, convIdFromResponse);

    // Complete the response — same pipeline as getConversationMessages so link/cover and table title rows match (processSSEContent includes age-range fix)
    const processedText = processSSEContent(cleanedResponse);
    const normalizedText =
      normalizeTableTitleRowsInAnswer(processedText).trim();
    const finalResponse = {
      request: userMessage,
      response: {
        text: normalizedText,
        suggestions: [
          "ما هي الخدمات الإحصائية المتاحة؟",
          "كيف يمكنني الحصول على البيانات؟",
          "ما هي مواعيد إصدار التقارير؟",
        ],
        metadata: {
          confidence: 0.9,
          category: "api",
          timestamp: new Date().toISOString(),
          conv_id: convIdFromResponse, // Include conv_id from headers or response
          messageId: messageIdFromResponse, // Include messageId from headers or response
        },
        graph: graphData, // Include graph data if present
        excelCode: excelCode, // v1: generate_excel event content (Python script or code)
        links: linksData, // Include links data if present
        links_cover: linksCoverData, // Include links_cover data if present
        conv_id: convIdFromResponse, // Also at response level for easy access
        messageId: messageIdFromResponse, // Also at response level for easy access
        canExport:
          canExportFromStream === true || canExportFromStream === "true", // From stream details payload; always boolean for UI
      },
    };

    onComplete(finalResponse);
  } catch (error) {
    console.error("Real API Error:", error);
    throw error;
  }
};

// Simulate Server Sent Events streaming with API fallback
export const streamChatResponse = async (
  userMessage,
  chatId, // Chat ID to generate unique conv_id for each conversation
  abortSignal,
  onChunk,
  onInfo,
  onComplete,
  onError,
  token = null, // Authorization token
  userId = null, // User ID
  convId = null, // conv_id from chatHistory (if exists), otherwise null
  onEventChange = null, // v1 only: called when SSE event type changes; UI can clear and show only latest event content
) => {
  try {
    // استخدام آخر conv_id مخزن لهذه المحادثة إذا لم يُمرَّر (مثلاً عندما الهيدر السابق لم يكن متاحاً أو لم يُحدَّث convIdRef)
    const resolvedConvId =
      convId ?? (chatId ? (conversationIdByChatId.get(chatId) ?? null) : null);
    // Try real API first
    await callRealAPI(
      userMessage,
      chatId, // Pass chatId instead of userToken
      abortSignal,
      onChunk,
      onInfo,
      onComplete,
      onError,
      token, // Pass token for authorization header
      userId, // Pass userId for userId header
      resolvedConvId, // Pass conv_id: from caller, or from stored state for this chat
      onEventChange, // v1: notify when event type changes so UI can show only latest event
    );
  } catch (apiError) {
    // User stopped the stream: do not fall back to error message, rethrow
    const isAbort =
      apiError?.name === "AbortError" ||
      (apiError?.message &&
        String(apiError.message).toLowerCase().includes("abort"));
    if (isAbort) {
      throw apiError;
    }

    console.warn("API failed, falling back to mock data:", apiError);

    // Fallback to mock data
    try {
      //       data: {"status": "info", "message": "جاري تحليل السؤال"}

      // data: {"status": "info", "message": "جارى البحث فى البيانات"}

      // Simulate API delay
      await delay(500);

      const match = findBestMatch(userMessage);

      if (!match) {
        // Detect language of the user message
        const userLanguage = detectLanguage(userMessage);

        // Default response for unmatched queries - based on user's language
        const defaultResponse = {
          request: userMessage,
          response: {
            text:
              userLanguage === "en"
                ? `**An unexpected technical error occurred. Please try again and contact support if the problem persists.**

Or you can refer to the main data source: [https://capmas.gov.eg](https://capmas.gov.eg)`
                : `**حدث خطأ غير متوقع، يرجى المحاولة مرة أخرى والتواصل مع الدعم إذا استمرت المشكلة.**

أو يمكنك الرجوع إلى المصدر الرئيسي للبيانات: [https://capmas.gov.eg](https://capmas.gov.eg)`,
            suggestions:
              userLanguage === "en"
                ? [
                    "Labor market and migration",
                    "How to get labor market data",
                    "Official website",
                    "CAPMAS data source",
                  ]
                : [
                    "سوق العمل والهجرة",
                    "كيفية الحصول على بيانات سوق العمل",
                    "الموقع الرسمي",
                    "مصدر بيانات الجهاز",
                  ],
            metadata: {
              confidence: 0.3,
              category: "unknown",
              timestamp: new Date().toISOString(),
            },
          },
        };

        // Stream the default response
        await streamText(defaultResponse.response.text, onChunk);
        onComplete(defaultResponse);
        return;
      }

      // Resolve text and suggestions by language (mockApiData has text_ar/text_en, suggestions_ar/suggestions_en)
      const userLanguage = detectLanguage(userMessage);
      const text =
        match.response.text_ar != null && match.response.text_en != null
          ? userLanguage === "en"
            ? match.response.text_en
            : match.response.text_ar
          : match.response.text;
      const suggestions =
        match.response.suggestions_ar != null &&
        match.response.suggestions_en != null
          ? userLanguage === "en"
            ? match.response.suggestions_en
            : match.response.suggestions_ar
          : match.response.suggestions;

      // Stream the matched response
      await streamText(text, onChunk);

      // Stream details if available
      if (match.response.details && match.response.details.length > 0) {
        await delay(200);
        for (const detail of match.response.details) {
          await streamText(detail, onChunk);
          await delay(100);
        }
      }

      onComplete({
        request: match.request,
        response: {
          ...match.response,
          text,
          suggestions: suggestions ?? match.response.suggestions,
          details: match.response.details ?? [],
          graph: match.response.graph ?? null,
          links: match.response.links ?? null,
          links_cover: match.response.links_cover ?? null,
          canExport: match.response.canExport ?? false,
        },
      });
    } catch (error) {
      console.error("Mock data streaming error:", error);
      onError(error);
    }
  }
};

// Simulate text streaming character by character
const streamText = async (text, onChunk) => {
  // Validate onChunk is a function
  if (typeof onChunk !== "function") {
    console.warn("streamText: onChunk is not a function, skipping streaming");
    return;
  }

  // Validate text is a string
  if (typeof text !== "string") {
    console.warn("streamText: text is not a string, converting to string");
    text = String(text || "");
  }

  const words = text.split(" ");

  for (let i = 0; i < words.length; i++) {
    const word = words[i] + (i < words.length - 1 ? " " : "");
    if (typeof onChunk === "function") {
      onChunk(word);
    }
    await delay(50 + Math.random() * 100); // Random delay between 50-150ms
  }
};

function transformConversationListItem(conv) {
  if (!conv || typeof conv !== "object") return null;
  return {
    id: conv.conversationId || conv._id,
    conversationId: conv.conversationId || null,
    title: conv.title || "",
    createdAt: conv.createdAt ? new Date(conv.createdAt) : new Date(),
    userId: conv.userId,
    _id: conv._id,
    messages: [],
  };
}

function conversationGroupKeyFromLabel(label) {
  const k = String(label || "other")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "");
  return k || "other";
}

function parseChatHistoryResponse(data) {
  const paginationRaw = data?.pagination || {};
  const page = Math.max(1, Number(paginationRaw.page) || 1);
  const limit = Math.max(1, Math.min(100, Number(paginationRaw.limit) || 20));
  const totalConvs = Number(
    paginationRaw.total_convs ?? paginationRaw.totalConvs ?? 0,
  );
  const totalPages = Number(
    paginationRaw.total_pages ?? paginationRaw.totalPages ?? 0,
  );
  const hasMore =
    totalPages > 0
      ? page < totalPages
      : totalConvs > 0
        ? page * limit < totalConvs
        : false;

  const pagination = {
    page,
    limit,
    totalConvs,
    totalPages,
    hasMore,
  };

  const raw = data?.conversations;
  if (raw == null) {
    return { groups: [], pagination };
  }

  if (Array.isArray(raw)) {
    const chats = raw.map(transformConversationListItem).filter(Boolean);
    return {
      groups: [
        {
          key: "all",
          label: "All",
          chats,
        },
      ],
      pagination,
    };
  }

  if (typeof raw === "object") {
    const groups = [];
    for (const label of Object.keys(raw)) {
      const list = raw[label];
      if (!Array.isArray(list)) continue;
      const key = conversationGroupKeyFromLabel(label);
      groups.push({
        key,
        label: String(label).trim(),
        chats: list.map(transformConversationListItem).filter(Boolean),
      });
    }
    return { groups, pagination };
  }

  return { groups: [], pagination };
}

// Get chat history from real API (grouped buckets + pagination for infinite scroll)
export const getChatHistory = async (
  token = null,
  userId = null,
  options = {},
) => {
  try {
    // Validate CHATBOT_API_BASE_URL
    if (!END_POINTS.CHATBOT_API_BASE_URL || typeof END_POINTS.CHATBOT_API_BASE_URL !== "string") {
      throw new Error(
        "Invalid API_BASE_URL: API_BASE_URL must be a valid string",
      );
    }

    // Clean and normalize CHATBOT_API_BASE_URL (remove trailing slashes)
    const cleanApiBaseUrl = END_POINTS.CHATBOT_API_BASE_URL.trim().replace(/\/+$/, "");

    let endpoint;

    if (API_VERSION === "v2") {
      endpoint = `${cleanApiBaseUrl}v2/api/conversations`;
    } else {
      endpoint = `${cleanApiBaseUrl}/api/conversations`;
    }

    // Validate endpoint
    if (!endpoint || typeof endpoint !== "string" || endpoint.trim() === "") {
      throw new Error(
        `Invalid endpoint: endpoint must be a valid non-empty string. Got: ${endpoint}`,
      );
    }

    let requestUrl;
    try {
      requestUrl = new URL(endpoint);
    } catch (error) {
      throw new Error(
        `Invalid endpoint URL: ${endpoint}. Must be a valid HTTP/HTTPS URL. Error: ${error.message}`,
      );
    }

    const page = Math.max(1, parseInt(options.page, 10) || 1);
    const limit = Math.min(100, Math.max(1, parseInt(options.limit, 10) || 20));
    requestUrl.searchParams.set("page", String(page));
    requestUrl.searchParams.set("limit", String(limit));

    // Prepare headers
    const headers = {
      "Content-Type": "application/json",
    };

    // Add authorization header if token is provided
    if (token != null && token !== "") {
      const tokenString = String(token).trim();
      if (tokenString) {
        headers["authorization"] = tokenString;
      }
    }

    // Add userId header if userId is provided
    if (userId != null && userId !== "") {
      const userIdString = String(userId).trim();
      if (userIdString) {
        headers["userId"] = userIdString;
      }
    }

    // Validate headers - ensure all values are strings
    for (const [key, value] of Object.entries(headers)) {
      if (typeof value !== "string") {
        throw new Error(
          `Invalid header value for "${key}": must be a string. Got: ${typeof value}`,
        );
      }
      if (value === "") {
        throw new Error(`Invalid header value for "${key}": cannot be empty`);
      }
    }

    const response = await fetch(requestUrl.toString(), {
      method: "GET",
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return parseChatHistoryResponse(data);
  } catch (error) {
    console.error("Failed to fetch chat history from API:", error);
    return {
      groups: [],
      pagination: {
        page: 1,
        limit: 20,
        totalConvs: 0,
        totalPages: 0,
        hasMore: false,
      },
    };
  }
};

// Simulate API call for saving chat
export const saveChat = async (chatData, token = null, userId = null) => {
  try {
    // If token and userId are provided, use API
    if (token && userId && END_POINTS.CHATBOT_API_BASE_URL) {
      // Validate CHATBOT_API_BASE_URL
      if (!END_POINTS.CHATBOT_API_BASE_URL || typeof END_POINTS.CHATBOT_API_BASE_URL !== "string") {
        throw new Error(
          "Invalid CHATBOT_API_BASE_URL: CHATBOT_API_BASE_URL must be a valid string",
        );
      }

      // Clean and normalize CHATBOT_API_BASE_URL (remove trailing slashes)
      const cleanApiBaseUrl = END_POINTS.CHATBOT_API_BASE_URL.trim().replace(/\/+$/, "");

      let endpoint;
      if (API_VERSION === "v2") {
        endpoint = `${cleanApiBaseUrl}v2/api/conversations`;
      } else {
        endpoint = `${cleanApiBaseUrl}/api/conversations`;
      }

      // Prepare headers
      const headers = {
        "Content-Type": "application/json",
      };

      // Add authorization header if token is provided
      if (token != null && token !== "") {
        const tokenString = String(token).trim();
        if (tokenString) {
          headers["authorization"] = tokenString;
        }
      }

      // Add userId header if userId is provided
      if (userId != null && userId !== "") {
        const userIdString = String(userId).trim();
        if (userIdString) {
          headers["userId"] = userIdString;
        }
      }

      // Prepare request body
      const requestBody = {
        id: chatData.id,
        conversationId: chatData.conversationId || chatData.conv_id || null, // Include conversationId if available
        title: chatData.title,
        createdAt: chatData.createdAt,
        lastMessage: chatData.lastMessage,
      };

      // Make API call
      const response = await fetch(endpoint, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      // Parse response
      const data = await response.json().catch(() => {
        // If response is not JSON, return success anyway
        return { success: true };
      });

      // Return updated history (or current chatData if API doesn't return history)
      return data.conversations || data.history || [chatData];
    } else {
      // Fallback to localStorage for backward compatibility
      await delay(200);
      const history = JSON.parse(
        localStorage.getItem("chatbot_history") || "[]",
      );
      const newHistory = [chatData, ...history.slice(0, 9)]; // Keep last 10 chats
      localStorage.setItem("chatbot_history", JSON.stringify(newHistory));
      return newHistory;
    }
  } catch (error) {
    console.error("Failed to save chat to API:", error);
    // Fallback to localStorage for backward compatibility
    await delay(200);
    const history = JSON.parse(localStorage.getItem("chatbot_history") || "[]");
    const newHistory = [chatData, ...history.slice(0, 9)]; // Keep last 10 chats
    localStorage.setItem("chatbot_history", JSON.stringify(newHistory));
    return newHistory;
  }
};

// Simulate API call for deleting chat
export const deleteChat = async (chatId) => {
  await delay(200);
  const history = JSON.parse(localStorage.getItem("chatbot_history") || "[]");
  const newHistory = history.filter((chat) => chat.id !== chatId);
  localStorage.setItem("chatbot_history", JSON.stringify(newHistory));
  return newHistory;
};

// Normalize answer text so "last rows" (section titles, وحدة القياس, وفقا للجهاز, links, COVER) appear outside tables (same as preprocessMarkdown in ChatBot for callRealAPI)
// Pipe-delimited lines that are titles/unit/attribution/links → blank line before + content without pipes so they render as paragraphs below the table
function normalizeTableTitleRowsInAnswer(text) {
  if (!text || typeof text !== "string") return text;
  let result = text;
  const titleOrLinkTest =
    /بحث\s*القوى|نشرة\s*إحصاءات|إحصاءات\s*الخدمات|وحدة\s*القياس|وفقا\s*ل(?:لجهاز|الجهاز)|لو\s*حابب|COVER|\[\s*C\s*OVER\s*\]|]\s*\(https?:\/\/|]\s*\(\//i;
  // 1) Consecutive table rows: if the second row looks like a title/link/unit/attribution, end the table before it and output as paragraph
  const twoRowPattern = /(\|\s*[^\n]+\|)\s*\r?\n(\|\s*([^\n]+?)\s*\|)/g;
  result = result.replace(
    twoRowPattern,
    (match, prevRow, _fullTitleRow, content) => {
      const c = content.trim();
      if (titleOrLinkTest.test(c)) return `${prevRow}\n\n${c}\n`;
      return match;
    },
  );
  // 2) Any remaining pipe-delimited line that contains title/link/cover/unit/attribution → render as paragraph (repeat until no change)
  let prev = "";
  while (prev !== result) {
    prev = result;
    result = result.replace(
      /\n(\|\s*([^\n]*?(?:بحث\s*القوى|نشرة|إحصاءات\s*الخدمات|وحدة\s*القياس|وفقا\s*ل(?:لجهاز|الجهاز)|لو\s*حابب|COVER|\[\s*C\s*OVER\s*\]|]\s*\(https?:\/\/|]\s*\(\/)[^\n]*?)\s*\|)/g,
      "\n\n$2\n",
    );
  }
  return result;
}

/** Normalize pagination from GET /conversations/:id/messages (snake_case + camelCase). */
function normalizeConversationMessagesPagination(
  rawPag,
  fallbackPage,
  fallbackLimit,
) {
  const p = rawPag && typeof rawPag === "object" ? rawPag : {};
  const page = Math.max(1, Number(p.page) || fallbackPage);
  const limit = Math.min(100, Math.max(1, Number(p.limit) || fallbackLimit));
  const totalMessages = Number(
    p.total_messages ?? p.totalMessages ?? p["total messages"] ?? 0,
  );
  let totalPages = Number(p.total_pages ?? p.totalPages ?? 0);
  if (totalPages <= 0 && totalMessages > 0 && limit > 0) {
    totalPages = Math.ceil(totalMessages / limit);
  }
  const hasMore = totalPages > 0 ? page < totalPages : false;
  return {
    page,
    limit,
    totalMessages,
    totalPages,
    hasMore,
  };
}

// Simulate API call for feedback
/**
 * GET conversation messages (paged). The UI loads **page 1** when opening a thread, then
 * **page 2, 3, …** on scroll-up. Each page’s `messages` may list **newest exchange first**; we sort
 * to **oldest → newest** before rendering.
 */
export const getConversationMessages = async (
  convId,
  token = null,
  userId = null,
  page = 1,
  limit = 5,
) => {
  try {
    // Validate CHATBOT_API_BASE_URL
    if (!END_POINTS.CHATBOT_API_BASE_URL || typeof END_POINTS.CHATBOT_API_BASE_URL !== "string") {
      throw new Error(
        "Invalid CHATBOT_API_BASE_URL: CHATBOT_API_BASE_URL must be a valid string",
      );
    }

    // Validate convId
    if (!convId || typeof convId !== "string") {
      throw new Error(
        "Invalid convId: convId must be a valid non-empty string",
      );
    }

    // Validate page and limit
    const pageNum = Math.max(1, parseInt(String(page), 10) || 1);
    const limitNum = Math.min(
      100,
      Math.max(1, parseInt(String(limit), 10) || 5),
    );

    if (pageNum < 1) {
      throw new Error("Invalid page: page must be >= 1");
    }
    if (limitNum < 1 || limitNum > 100) {
      throw new Error("Invalid limit: limit must be between 1 and 100");
    }

    // Clean and normalize CHATBOT_API_BASE_URL (remove trailing slashes)
    const cleanApiBaseUrl = END_POINTS.CHATBOT_API_BASE_URL.trim().replace(/\/+$/, "");

    let endpoint;

    if (API_VERSION === "v2") {
      endpoint = `${cleanApiBaseUrl}v2/api/conversations/${convId}/messages`;
    } else {
      endpoint = `${cleanApiBaseUrl}/api/conversations/${convId}/messages`;
    }

    // Add query parameters
    const url = new URL(endpoint);
    url.searchParams.append("page", pageNum.toString());
    url.searchParams.append("limit", limitNum.toString());
    const finalEndpoint = url.toString();

    // Validate endpoint
    if (
      !finalEndpoint ||
      typeof finalEndpoint !== "string" ||
      finalEndpoint.trim() === ""
    ) {
      throw new Error(
        `Invalid endpoint: endpoint must be a valid non-empty string. Got: ${finalEndpoint}`,
      );
    }

    // Prepare headers
    const headers = {
      "Content-Type": "application/json",
    };

    // Add authorization header if token is provided
    if (token != null && token !== "") {
      const tokenString = String(token).trim();
      if (tokenString) {
        headers["authorization"] = tokenString;
      }
    }

    // Add userId header if userId is provided
    if (userId != null && userId !== "") {
      const userIdString = String(userId).trim();
      if (userIdString) {
        headers["userId"] = userIdString;
      }
    }

    // Make API call
    const response = await fetch(finalEndpoint, {
      method: "GET",
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    // Parse response
    const data = await response.json();

    const pagination = normalizeConversationMessagesPagination(
      data.pagination,
      pageNum,
      limitNum,
    );

    // Helper function to extract links, links_cover, and graph from text
    const extractMetadataFromText = (text) => {
      let cleanedText = text || "";
      let linksData = null;
      let linksCoverData = null;
      let graphData = null;

      // First, convert <br> to newlines (same replaceBrTags as callRealAPI)
      let processedText = replaceBrTags(cleanedText);

      // Look for links: {...} (with possible newlines/br tags)
      const linksPattern = /links:\s*\{([\s\S]*?)\}/;
      const linksMatch = processedText.match(linksPattern);
      if (linksMatch) {
        try {
          const linksStr = "{" + linksMatch[1] + "}";
          // Convert Python-style dict to JSON (handle single quotes and newlines)
          const linksJson = linksStr.replace(/'/g, '"').replace(/\n/g, "");
          linksData = JSON.parse(linksJson);

          // Fix broken URLs in links
          Object.keys(linksData).forEach((key) => {
            if (linksData[key]) {
              linksData[key] = linksData[key]
                .replace(/https:\s+\/\//g, "https://")
                .replace(/http:\s+\/\//g, "http://")
                .replace(/:\s+(\d+)/g, ":$1"); // Fix port numbers like ": 8080" -> ":8080"
            }
          });

          // Remove from text
          cleanedText = cleanedText
            .replace(/links:\s*\{[\s\S]*?\}/g, "")
            .trim();
          processedText = processedText.replace(linksPattern, "").trim();
        } catch (e) {
          console.error("Failed to parse links:", e);
        }
      }

      // Look for links_cover: {...} (with possible newlines/br tags)
      const linksCoverPattern = /links_cover:\s*\{([\s\S]*?)\}/;
      const linksCoverMatch = processedText.match(linksCoverPattern);
      if (linksCoverMatch) {
        try {
          const linksCoverStr = "{" + linksCoverMatch[1] + "}";
          // Convert Python-style dict to JSON (handle single quotes and newlines)
          const linksCoverJson = linksCoverStr
            .replace(/'/g, '"')
            .replace(/\n/g, "");
          linksCoverData = JSON.parse(linksCoverJson);

          // Fix broken URLs in links_cover
          Object.keys(linksCoverData).forEach((key) => {
            if (linksCoverData[key]) {
              linksCoverData[key] = linksCoverData[key]
                .replace(/https:\s+\/\//g, "https://")
                .replace(/http:\s+\/\//g, "http://")
                .replace(/:\s+(\d+)/g, ":$1"); // Fix port numbers like ": 8080" -> ":8080"
            }
          });

          // Remove from text
          cleanedText = cleanedText
            .replace(/links_cover:\s*\{[\s\S]*?\}/g, "")
            .trim();
          processedText = processedText.replace(linksCoverPattern, "").trim();
        } catch (e) {
          console.error("Failed to parse links_cover:", e);
        }
      }

      // Extract graph data - check for JSON object format first
      // Look for pattern: data: {"graph": "..."} or {"graph": "..."}
      const combinedText = cleanedText.trim();

      // Remove "data: " prefix if present at the start
      let textToSearch = combinedText;
      const dataPrefixMatch = textToSearch.match(/^data:\s*/i);
      if (dataPrefixMatch) {
        textToSearch = textToSearch.substring(dataPrefixMatch[0].length).trim();
      }

      // First, try to find and parse complete JSON object containing "graph"
      if (textToSearch.includes('"graph"')) {
        try {
          // Find all potential JSON objects containing "graph"
          // Start from the end and work backwards
          let searchIndex = textToSearch.length;
          while (searchIndex >= 0) {
            const lastBraceIndex = textToSearch.lastIndexOf(
              "{",
              searchIndex - 1,
            );
            if (lastBraceIndex === -1) break;

            // Extract from this brace to the end
            const jsonCandidate = textToSearch.substring(lastBraceIndex);

            // Try to find the matching closing brace by counting braces
            // But be careful with braces inside strings
            let braceCount = 0;
            let endIndex = -1;
            let inString = false;
            let escapeNext = false;

            for (let i = 0; i < jsonCandidate.length; i++) {
              const char = jsonCandidate[i];

              if (escapeNext) {
                escapeNext = false;
                continue;
              }

              if (char === "\\") {
                escapeNext = true;
                continue;
              }

              if (char === '"' && !escapeNext) {
                inString = !inString;
                continue;
              }

              if (!inString) {
                if (char === "{") braceCount++;
                if (char === "}") {
                  braceCount--;
                  if (braceCount === 0) {
                    endIndex = i + 1;
                    break;
                  }
                }
              }
            }

            if (endIndex !== -1) {
              const jsonString = jsonCandidate.substring(0, endIndex);

              // Check if it contains "graph" key
              if (jsonString.includes('"graph"')) {
                try {
                  // Try to parse as JSON
                  const jsonData = JSON.parse(jsonString);
                  if (typeof jsonData.graph === "string") {
                    const trimmed = jsonData.graph.trim();
                    if (trimmed) graphData = jsonData.graph;
                    // Always remove the JSON from cleanedText (including empty {"graph": ""})
                    const jsonToRemove = textToSearch.substring(
                      lastBraceIndex,
                      lastBraceIndex + endIndex,
                    );
                    if (dataPrefixMatch) {
                      cleanedText = combinedText
                        .replace(dataPrefixMatch[0] + jsonToRemove, "")
                        .trim();
                    } else {
                      cleanedText = combinedText
                        .replace(jsonToRemove, "")
                        .trim();
                    }
                    break;
                  }
                } catch (parseError) {
                  // Continue searching
                }
              }
            }

            // Move search index backwards
            searchIndex = lastBraceIndex - 1;
          }
        } catch (error) {
          // Ignore errors and try Python dict format
        }
      }

      // Also check for Python dict format: {'graph': '...'}
      if (!graphData) {
        const graphStartPattern = "{'graph': '";
        const graphStartIndex = cleanedText.lastIndexOf(graphStartPattern);
        if (graphStartIndex !== -1) {
          const graphDictString = cleanedText.substring(graphStartIndex);
          if (graphDictString.endsWith("'}")) {
            const graphStart = graphStartPattern.length;
            const graphContent = graphDictString.substring(
              graphStart,
              graphDictString.length - 2,
            );
            const decoded = graphContent
              .replace(/\\n/g, "\n")
              .replace(/\\r/g, "\r")
              .replace(/\\t/g, "\t")
              .replace(/\\"/g, '"')
              .replace(/\\'/g, "'")
              .replace(/\\\\/g, "\\")
              .replace(/https:\s+\/\//g, "https://")
              .replace(/http:\s+\/\//g, "http://")
              .replace(/:\s+(\d+)/g, ":$1");
            if (decoded.trim()) graphData = decoded;
            cleanedText = cleanedText.substring(0, graphStartIndex).trim();
          }
        }
      }

      // Strip any remaining empty graph JSON (e.g. {"graph": ""} or {\"graph\": \"\"}) so it never shows in UI
      cleanedText = cleanedText
        .replace(/\{\s*["']graph["']\s*:\s*[""]\s*\}/g, "")
        .replace(/\{\s*["']graph["']\s*:\s*['']\s*\}/g, "")
        .replace(/\{\s*\\"graph\\"\s*:\s*\\"\s*\\"\s*\}/g, "")
        .trim();

      // Convert <br> tags to newlines (same replaceBrTags as callRealAPI)
      cleanedText = replaceBrTags(cleanedText);

      return {
        cleanedText,
        links: linksData,
        links_cover: linksCoverData,
        graph: graphData,
      };
    };

    // Transform API response to match expected format
    // API returns: { messages: [{ question, answer, messageId, likes, dislikes }], pagination: {...} }
    // Expected format: [{ id, text, isUser, likes, dislikes, ... }]
    const transformedMessages = (data.messages || []).flatMap((msg) => {
      const messages = [];
      // Get messageId from response (used for like/dislike API calls)
      const messageId = msg.messageId || msg.messagedId; // Support both formats for backward compatibility

      // Add user message (question)
      if (msg.question && msg.question.trim() !== "") {
        messages.push({
          id: messageId
            ? `conv-user-${messageId}`
            : `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          text: msg.question.trim(),
          isUser: true,
          timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
          likes: 0,
          dislikes: 0,
          messageId: messageId, // Keep original ID for like/dislike API calls
        });
      }

      // Add bot message (answer) - extract metadata (links, links_cover, graph)
      if (msg.answer && msg.answer.trim() !== "") {
        const metadata = extractMetadataFromText(msg.answer);
        // Same processing as streamed content: <br> → newline, bold/bullets/links normalized (processSSEContent)
        const processedAnswerText = processSSEContent(metadata.cleanedText);
        // Same as callRealAPI: last 2 rows that are titles/links for the next table → outside table (normalizeTableTitleRowsInAnswer)
        const normalizedAnswer =
          normalizeTableTitleRowsInAnswer(processedAnswerText);
        messages.push({
          id: messageId
            ? `conv-bot-${messageId}`
            : `bot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          text: normalizedAnswer.trim(),
          isUser: false,
          timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
          likes: msg.likes || 0,
          dislikes: msg.dislikes || 0,
          messageId: messageId, // Keep original ID for like/dislike API calls
          links: metadata.links, // Include links if present
          links_cover: metadata.links_cover, // Include links_cover if present
          graph: (metadata.graph && String(metadata.graph).trim()) || undefined, // Only if non-empty
          canExport: msg.canExport === true, // From API: show export action only when true
        });
      }

      return messages;
    });

    // Newest-first in API array → chat needs oldest at top, newest at bottom
    transformedMessages.sort((a, b) => {
      const ta =
        a.timestamp instanceof Date ? a.timestamp.getTime() : Number(a.timestamp) || 0;
      const tb =
        b.timestamp instanceof Date ? b.timestamp.getTime() : Number(b.timestamp) || 0;
      if (ta !== tb) return ta - tb;
      if (a.isUser !== b.isUser) return a.isUser ? -1 : 1;
      return String(a.id).localeCompare(String(b.id));
    });

    // Return both messages and pagination info
    return {
      messages: transformedMessages,
      pagination: {
        page: pagination.page,
        limit: pagination.limit,
        totalMessages: pagination.totalMessages,
        totalPages: pagination.totalPages,
        hasMore: pagination.hasMore,
      },
    };
  } catch (error) {
    console.error(
      `Failed to get conversation messages for convId ${convId}:`,
      error,
    );
    throw error;
  }
};

// Submit feedback (like/dislike) to real API
export const submitFeedback = async (messageId, feedback, token, userId) => {
  try {
    // Validate CHATBOT_API_BASE_URL
    if (!END_POINTS.CHATBOT_API_BASE_URL || typeof END_POINTS.CHATBOT_API_BASE_URL !== "string") {
      throw new Error(
        "Invalid CHATBOT_API_BASE_URL: CHATBOT_API_BASE_URL must be a valid string",
      );
    }

    // Validate messageId
    if (!messageId || typeof messageId !== "string") {
      throw new Error(
        "Invalid messageId: messageId must be a valid non-empty string",
      );
    }

    // Validate feedback
    if (feedback !== "like" && feedback !== "dislike") {
      throw new Error(
        `Invalid feedback: must be "like" or "dislike". Got: ${feedback}`,
      );
    }

    // Clean and normalize CHATBOT_API_BASE_URL (remove trailing slashes)
    const cleanApiBaseUrl = END_POINTS.CHATBOT_API_BASE_URL.trim().replace(/\/+$/, "");

    let endpoint;

    if (API_VERSION === "v2") {
      endpoint = `${cleanApiBaseUrl}v2/api/messages/${messageId}/${feedback}`;
    } else {
      endpoint = `${cleanApiBaseUrl}/api/messages/${messageId}/${feedback}`;
    }

    // Validate endpoint
    if (!endpoint || typeof endpoint !== "string" || endpoint.trim() === "") {
      throw new Error(
        `Invalid endpoint: endpoint must be a valid non-empty string. Got: ${endpoint}`,
      );
    }

    // Validate endpoint is a valid URL
    try {
      new URL(endpoint);
    } catch (error) {
      throw new Error(
        `Invalid endpoint URL: ${endpoint}. Must be a valid HTTP/HTTPS URL. Error: ${error.message}`,
      );
    }

    // Prepare headers (no token or userId needed as per user requirement)
    const headers = {
      "Content-Type": "application/json",
    };

    // Add authorization header if token is provided
    if (token != null && token !== "") {
      const tokenString = String(token).trim();
      if (tokenString) {
        headers["authorization"] = tokenString;
      }
    }

    // Add userId header if userId is provided
    if (userId != null && userId !== "") {
      const userIdString = String(userId).trim();
      if (userIdString) {
        headers["userId"] = userIdString;
      }
    }

    // Make API call
    const response = await fetch(endpoint, {
      method: "POST",
      headers: headers,
    });

    // Parse response first to check for error messages
    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      // If response is not JSON, check status
      if (!response.ok) {
        return {
          success: false,
          error: `API Error: ${response.status} ${response.statusText}`,
        };
      }
      // If response is ok but not JSON, return success
      return { success: true };
    }

    // Check if response contains error key
    if (!response.ok) {
      // Extract error message from response if available
      const errorMessage =
        data?.error ||
        data?.message ||
        `API Error: ${response.status} ${response.statusText}`;
      return {
        success: false,
        error: errorMessage,
      };
    }

    // Check if response indicates failure even with 200 status
    if (data.error) {
      return {
        success: false,
        error: data.error,
      };
    }

    return { success: true, data };
  } catch (error) {
    console.error(
      `Failed to submit ${feedback} feedback for message ${messageId}:`,
      error,
    );

    // Return error but don't throw to prevent UI issues
    return {
      success: false,
      error: error.message || "Failed to submit feedback",
    };
  }
};

// Export message API call
export const exportMessage = async (messageId, token = null, userId = null) => {
  try {
    const EXPORTED_EXCEL_FILENAME = "Exported_Data.xlsx";

    const buildClearExcelFilename = (id) => {
      const safeId = String(id || "unknown")
        .trim()
        .replace(/[^a-zA-Z0-9_-]/g, "_")
        .slice(0, 24);
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .replace("T", "_")
        .replace("Z", "");
      return `capmas_chatbot_export_${safeId}_${timestamp}.xlsx`;
    };

    const isUuidLikeFilename = (name) => {
      const baseName = String(name || "")
        .trim()
        .replace(/\.[^.]+$/, "");
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        baseName,
      );
    };

    // Validate CHATBOT_API_BASE_URL
    if (!END_POINTS.CHATBOT_API_BASE_URL || typeof END_POINTS.CHATBOT_API_BASE_URL !== "string") {
      throw new Error(
        "Invalid CHATBOT_API_BASE_URL: CHATBOT_API_BASE_URL must be a valid string",
      );
    }

    // Validate messageId
    if (!messageId || typeof messageId !== "string") {
      throw new Error(
        "Invalid messageId: messageId must be a valid non-empty string",
      );
    }

    // Clean and normalize CHATBOT_API_BASE_URL (remove trailing slashes)
    const cleanApiBaseUrl = END_POINTS.CHATBOT_API_BASE_URL.trim().replace(/\/+$/, "");

    let endpoint;

    if (API_VERSION === "v2") {
      endpoint = `${cleanApiBaseUrl}v2/api/conversations/${messageId}/export`;
    } else {
      endpoint = `${cleanApiBaseUrl}/api/conversations/${messageId}/export`;
    }

    // Validate endpoint
    if (!endpoint || typeof endpoint !== "string" || endpoint.trim() === "") {
      throw new Error(
        `Invalid endpoint: endpoint must be a valid non-empty string. Got: ${endpoint}`,
      );
    }

    // Validate endpoint is a valid URL
    try {
      new URL(endpoint);
    } catch (error) {
      throw new Error(
        `Invalid endpoint URL: ${endpoint}. Must be a valid HTTP/HTTPS URL. Error: ${error.message}`,
      );
    }

    // Prepare headers
    const headers = {
      "Content-Type": "application/json",
    };

    // Add authorization header if token is provided
    if (token != null && token !== "") {
      const tokenString = String(token).trim();
      if (tokenString) {
        headers["authorization"] = tokenString;
      }
    }

    // Add userId header if userId is provided
    if (userId != null && userId !== "") {
      const userIdString = String(userId).trim();
      if (userIdString) {
        headers["userId"] = userIdString;
      }
    }

    // Make API call with GET method
    const response = await fetch(endpoint, {
      method: "GET",
      headers: headers,
    });

    // Check Content-Type BEFORE reading body - body can only be read once
    const contentType = (
      response.headers.get("Content-Type") || ""
    ).toLowerCase();

    // Event Stream: read body once as text only (do not call .json() or .blob() after)
    if (contentType.includes("text/event-stream")) {
      const text = await response.text(); // Read body once
      if (!response.ok) {
        let errMsg = `API Error: ${response.status} ${response.statusText}`;
        try {
          const errJson = JSON.parse(text);
          errMsg = errJson.error || errJson.message || errMsg;
        } catch (_) {}
        return { success: false, error: errMsg };
      }
      // Stream consumed successfully; operation completed (e.g. export ran on server)
      return { success: true };
    }

    // File download types: read body once as blob
    if (
      contentType.includes("application/octet-stream") ||
      contentType.includes("application/pdf") ||
      contentType.includes("application/vnd.") ||
      contentType.includes("application/zip")
    ) {
      if (!response.ok) {
        const text = await response.text();
        let errMsg = `API Error: ${response.status} ${response.statusText}`;
        try {
          const errJson = JSON.parse(text);
          errMsg = errJson.error || errJson.message || errMsg;
        } catch (_) {}
        return { success: false, error: errMsg };
      }

      const xlsxMime =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const isExcelDownload =
        contentType.includes("spreadsheetml.sheet") ||
        contentType.includes(xlsxMime);

      // Prefer filename from Content-Disposition for non-Excel; Excel exports always use a fixed client name.
      let downloadFilename = isExcelDownload
        ? EXPORTED_EXCEL_FILENAME
        : buildClearExcelFilename(messageId);
      const contentDisposition = response.headers.get("Content-Disposition");
      if (!isExcelDownload && contentDisposition) {
        const filenameMatch =
          contentDisposition.match(
            /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i,
          ) || contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
        if (filenameMatch && filenameMatch[1]) {
          const candidateName = filenameMatch[1].trim();
          if (candidateName && !isUuidLikeFilename(candidateName)) {
            downloadFilename = candidateName;
          }
        }
      }

      const blob = await response.blob();
      const blobType = contentType.includes("spreadsheetml.sheet")
        ? xlsxMime
        : blob.type || undefined;
      const fileBlob = blobType ? new Blob([blob], { type: blobType }) : blob;
      const url = window.URL.createObjectURL(fileBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      return { success: true };
    }

    // For JSON or unknown: read body once as text, then parse if JSON
    const rawText = await response.text();
    let data;
    try {
      data = JSON.parse(rawText);
    } catch (parseError) {
      if (response.ok) {
        // Non-JSON but 200 - treat as success (e.g. plain text export)
        return { success: true };
      }
      return {
        success: false,
        error: `Failed to parse response: ${parseError.message}`,
      };
    }

    if (!response.ok) {
      const errorMessage =
        data?.error ||
        data?.message ||
        `API Error: ${response.status} ${response.statusText}`;
      return {
        success: false,
        error: errorMessage,
      };
    }

    // Check if response indicates failure even with 200 status
    if (data.error) {
      return {
        success: false,
        error: data.error,
      };
    }

    // Handle file download if response contains download URL or blob
    if (data.downloadUrl) {
      window.open(data.downloadUrl, "_blank");
    } else if (data.blob) {
      // Handle blob data
      const blob = new Blob([data.blob], {
        type: data.contentType || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fileNameFromResponse = data.filename || "";
      const fallbackFileName = buildClearExcelFilename(messageId);
      const isExcelBlob = String(data.contentType || "")
        .toLowerCase()
        .includes("spreadsheetml.sheet");
      a.download = isExcelBlob
        ? EXPORTED_EXCEL_FILENAME
        : fileNameFromResponse && !isUuidLikeFilename(fileNameFromResponse)
          ? fileNameFromResponse
          : fallbackFileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }

    return { success: true, data };
  } catch (error) {
    console.error(`Failed to export message ${messageId}:`, error);

    // Return error but don't throw to prevent UI issues
    return {
      success: false,
      error: error.message || "Failed to export message",
    };
  }
};

// Simulate API call for export (legacy function - kept for backward compatibility)
export const exportData = async (messageId, format) => {
  await delay(1000);
  // In real implementation, this would generate and download file
  return { success: true, downloadUrl: `#export-${messageId}-${format}` };
};

const TTS_MP3_MIME_CANDIDATES = [
  "audio/mpeg",
  'audio/mpeg; codecs="mp3"',
  "audio/mp3",
];

const getSupportedTtsMimeType = () => {
  if (typeof window === "undefined" || typeof window.MediaSource === "undefined")
    return null;
  return (
    TTS_MP3_MIME_CANDIDATES.find((type) =>
      window.MediaSource.isTypeSupported(type),
    ) || null
  );
};

// Text-to-Speech: Handle speech message with MediaSource API
export const handleSpeakMessage = async (
  text,
  {
    audioRef,
    ttsAbortControllerRef,
    isUserStoppedRef,
    setIsSpeaking,
    setSpeakingMessageId,
    setMessageActions,
    setIsAudioLoading,
    messageId,
    isSpeaking,
    speakingMessageId,
    shouldShowError,
    onTtsEnded,
    onError,
  },
) => {
  const canShowError = () => !shouldShowError || shouldShowError(messageId);
  const setSpeakAction = (id, value) => {
    setMessageActions((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        speak: value,
      },
    }));
  };
  const resetSpeakingUi = (id = messageId) => {
    if (setIsAudioLoading) setIsAudioLoading(false);
    setIsSpeaking(false);
    setSpeakingMessageId(null);
    setSpeakAction(id, false);
  };
  const stopAndClearAudio = () => {
    if (audioRef.current) {
      try {
        const src = audioRef.current.src;
        audioRef.current.pause();
        audioRef.current.src = "";
        if (src && src.startsWith("blob:")) {
          URL.revokeObjectURL(src);
        }
      } catch (e) {
        // Ignore cleanup errors
      }
      audioRef.current = null;
    }
    ttsAbortControllerRef.current = null;
  };

  // If already speaking this message, stop it
  if (isSpeaking && speakingMessageId === messageId) {
    // Abort any ongoing TTS request
    if (ttsAbortControllerRef.current) {
      ttsAbortControllerRef.current.abort();
      ttsAbortControllerRef.current = null;
    }
    stopAndClearAudio();
    resetSpeakingUi(messageId);
    return;
  }

  // Stop any current speech from a different message
  if (isSpeaking && speakingMessageId && speakingMessageId !== messageId) {
    // Abort any ongoing TTS request
    if (ttsAbortControllerRef.current) {
      ttsAbortControllerRef.current.abort();
      ttsAbortControllerRef.current = null;
    }
    stopAndClearAudio();
    if (setIsAudioLoading) setIsAudioLoading(false);
    setSpeakAction(speakingMessageId, false);
  }

  // Double-check: Ensure any existing audio is stopped before starting new one
  stopAndClearAudio();

  // Ensure abort controller is cleared
  if (ttsAbortControllerRef.current) {
    try {
      ttsAbortControllerRef.current.abort();
    } catch (e) {
      // Ignore errors during cleanup
    }
    ttsAbortControllerRef.current = null;
  }

  if (!text || text.trim().length === 0) return;

  // Set speaking state
  setIsSpeaking(true);
  setSpeakingMessageId(messageId);
  setSpeakAction(messageId, true);

  try {
    // Create abort controller for this request
    ttsAbortControllerRef.current = new AbortController();

    // Prepare headers
    const headers = {
      "Content-Type": "application/json",
    };

    // // Add authorization header if token is provided
    // if (token) {
    //   headers["authorization"] = token;
    // }

    // // Add userId header if userId is provided
    // if (userId) {
    //   headers["userId"] = String(userId);
    // }

    // Use fetch API for proper streaming support
    const response = await fetch(
      `${END_POINTS.SPEECH_API_BASE_URL}/text-to-speech-stream`,
      {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
          text: text,
          voiceName: null,
        }),
        signal: ttsAbortControllerRef.current.signal,
      },
    );

    if (!response.ok) {
      throw new Error(
        `TTS API Error: ${response.status} ${response.statusText}`,
      );
    }

    if (!response.body) {
      throw new Error("ReadableStream not supported in this browser");
    }

    // Signal UI that audio bytes are being downloaded.
    if (setIsAudioLoading) setIsAudioLoading(true);

    const supportedMimeType = getSupportedTtsMimeType();
    const shouldUseMediaSource = Boolean(supportedMimeType && window.MediaSource);
    const reader = response.body.getReader();

    let playbackFinished = false;
    let playbackFailed = false;
    let activeObjectUrl = null;

    const cleanupPlayback = () => {
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.src = "";
        } catch (e) {
          // Ignore cleanup errors
        }
        audioRef.current.onended = null;
        audioRef.current.onerror = null;
        audioRef.current = null;
      }
      if (activeObjectUrl) {
        try {
          URL.revokeObjectURL(activeObjectUrl);
        } catch (e) {
          // Ignore cleanup errors
        }
        activeObjectUrl = null;
      }
      ttsAbortControllerRef.current = null;
    };

    const markPlaybackEnded = () => {
      if (isUserStoppedRef && isUserStoppedRef.current) {
        cleanupPlayback();
        return;
      }
      playbackFinished = true;
      if (onTtsEnded) onTtsEnded();
      resetSpeakingUi(messageId);
      cleanupPlayback();
    };

    const handlePlaybackFailure = (err) => {
      if (playbackFinished || playbackFailed) return;
      playbackFailed = true;
      if (isUserStoppedRef && isUserStoppedRef.current) {
        cleanupPlayback();
        return;
      }
      console.error("TTS audio error:", err);
      resetSpeakingUi(messageId);
      cleanupPlayback();
      if (canShowError() && onError) onError(err);
    };

    const attachAudioHandlers = (audio) => {
      audio.onended = () => {
        markPlaybackEnded();
      };
      audio.onerror = () => {
        handlePlaybackFailure(new Error(audio.error?.message || "Audio error"));
      };
    };

    const playAudioElement = (audio) => {
      audio.play().catch((err) => {
        handlePlaybackFailure(err);
      });
    };

    const playViaBlobFallback = async () => {
      const chunks = [];
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (value && value.byteLength > 0) chunks.push(value);
        }
      } catch (err) {
        if (err.name === "AbortError") throw err;
        // Keep partial chunks for compatibility with abrupt connection close.
      } finally {
        try {
          reader.releaseLock();
        } catch (e) {
          // Ignore
        }
      }

      if (chunks.length === 0) {
        throw new Error("TTS API returned an empty audio response");
      }

      if (isUserStoppedRef && isUserStoppedRef.current) return;

      const totalBytes = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
      const combined = new Uint8Array(totalBytes);
      let offset = 0;
      for (const chunk of chunks) {
        combined.set(chunk, offset);
        offset += chunk.byteLength;
      }

      const audio = new Audio();
      activeObjectUrl = URL.createObjectURL(
        new Blob([combined], { type: supportedMimeType || "audio/mpeg" }),
      );
      audio.src = activeObjectUrl;
      audioRef.current = audio;
      attachAudioHandlers(audio);
      if (setIsAudioLoading) setIsAudioLoading(false);
      playAudioElement(audio);
    };

    const playViaMediaSource = async () => {
      const mediaSource = new window.MediaSource();
      const audio = new Audio();
      activeObjectUrl = URL.createObjectURL(mediaSource);
      audio.src = activeObjectUrl;
      audioRef.current = audio;
      attachAudioHandlers(audio);
      playAudioElement(audio);

      await new Promise((resolve, reject) => {
        let sourceBuffer = null;
        const pendingChunks = [];
        let streamEnded = false;
        let firstChunkAppended = false;
        let didReceiveAnyChunk = false;

        const appendNextChunk = () => {
          if (!sourceBuffer || sourceBuffer.updating) return;
          if (pendingChunks.length === 0) return;
          const nextChunk = pendingChunks.shift();
          try {
            sourceBuffer.appendBuffer(nextChunk);
            if (!firstChunkAppended) {
              firstChunkAppended = true;
              if (setIsAudioLoading) setIsAudioLoading(false);
            }
          } catch (appendError) {
            reject(appendError);
          }
        };

        const maybeFinalizeStream = () => {
          if (!sourceBuffer || sourceBuffer.updating) return;
          if (!streamEnded || pendingChunks.length > 0) return;
          if (mediaSource.readyState === "open") {
            try {
              mediaSource.endOfStream();
            } catch (e) {
              // Ignore endOfStream race errors
            }
          }
          resolve();
        };

        const onSourceOpen = async () => {
          try {
            sourceBuffer = mediaSource.addSourceBuffer(supportedMimeType);
            sourceBuffer.mode = "sequence";
          } catch (sourceBufferError) {
            reject(sourceBufferError);
            return;
          }

          sourceBuffer.addEventListener("updateend", () => {
            appendNextChunk();
            maybeFinalizeStream();
          });
          sourceBuffer.addEventListener("error", () => {
            reject(new Error("SourceBuffer error"));
          });

          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) {
                streamEnded = true;
                if (!didReceiveAnyChunk) {
                  reject(new Error("TTS API returned an empty audio response"));
                  return;
                }
                maybeFinalizeStream();
                break;
              }
              if (value && value.byteLength > 0) {
                didReceiveAnyChunk = true;
                pendingChunks.push(value);
                appendNextChunk();
              }
            }
          } catch (readError) {
            if (readError.name === "AbortError") {
              reject(readError);
              return;
            }
            // Keep already received chunks if stream closes unexpectedly.
            streamEnded = true;
            if (!didReceiveAnyChunk) {
              reject(readError);
              return;
            }
            maybeFinalizeStream();
          } finally {
            try {
              reader.releaseLock();
            } catch (e) {
              // Ignore
            }
          }
        };

        mediaSource.addEventListener("sourceopen", onSourceOpen, { once: true });
      });
    };

    if (shouldUseMediaSource) {
      await playViaMediaSource();
    } else {
      await playViaBlobFallback();
    }
  } catch (error) {
    if (setIsAudioLoading) setIsAudioLoading(false);

    if (error.name === "AbortError") {
      // User stopped the speech, just clean up
      resetSpeakingUi(messageId);
      stopAndClearAudio();
      return;
    }

    // Don't show error if user manually stopped the audio
    if (isUserStoppedRef && isUserStoppedRef.current) {
      resetSpeakingUi(messageId);
      stopAndClearAudio();
      return;
    }

    console.error("Text-to-Speech error:", error);
    resetSpeakingUi(messageId);
    stopAndClearAudio();

    if (canShowError() && onError) onError(error);
  }
};

// Speech-to-Text: Convert audio to text using backend API
export const speechToText = async (audioBlob, language = null) => {
  try {
    // Create FormData to send audio file
    const formData = new FormData();

    // Create audio file - should always be WAV format after conversion
    const audioFile = new File([audioBlob], "recording.wav", {
      type: "audio/wav",
    });

    formData.append("audioFile", audioFile);

    // Add language if specified
    if (language) {
      formData.append("language", language);
    }

    // Prepare headers
    const headers = {
      "Content-Type": "multipart/form-data",
    };

    // // Add authorization header if token is provided
    // if (token) {
    //   headers['authorization'] = token;
    // }

    // // Add userId header if userId is provided
    // if (userId) {
    //   headers['userId'] = String(userId);
    // }

    // Send to backend API
    const response = await axios.post(
      `${END_POINTS.SPEECH_API_BASE_URL}/speech-to-text`,
      formData,
      {
        headers: headers,
      },
    );

    // Check response
    if (response.data && response.data.state) {
      return {
        success: true,
        text: response.data.data?.text || "",
        language: response.data.data?.detectedLanguage || language,
      };
    } else {
      return {
        success: false,
        error: response.data.message || "Failed to transcribe audio",
      };
    }
  } catch (error) {
    console.error("Speech-to-text error:", error);
    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.message ||
        "Error transcribing audio",
    };
  }
};

export default {
  streamChatResponse,
  getChatHistory,
  saveChat,
  deleteChat,
  submitFeedback,
  exportData,
  handleSpeakMessage,
  speechToText,
};
